import { useState, useEffect, useRef, useCallback } from "react";
import { Auth, Nurses, Contracts, Interests, Documents, Categories, Schedules, Notifications, Messages as MessagesAPI, Audit, AdminPermissions, AccessCodes, getRefreshToken, clearTokens } from "./api";

// ============================================================
// UTILITIES
// ============================================================
function fmtDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return String(value);
  }
}

// ============================================================
// ICONS
// ============================================================
const Icon = ({ name, size = 20, color = "currentColor" }) => {
  const icons = {
    shield: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
    lock: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
    ),
    user: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    ),
    briefcase: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
      </svg>
    ),
    upload: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
      </svg>
    ),
    file: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
        <polyline points="13 2 13 9 20 9" />
      </svg>
    ),
    check: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    ),
    logout: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
        <polyline points="16 17 21 12 16 7" />
        <line x1="21" y1="12" x2="9" y2="12" />
      </svg>
    ),
    star: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    ),
    clock: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    ),
    dollar: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
    map: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
    ),
    bell: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
    ),
    menu: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="3" y1="12" x2="21" y2="12" />
        <line x1="3" y1="6" x2="21" y2="6" />
        <line x1="3" y1="18" x2="21" y2="18" />
      </svg>
    ),
    close: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    ),
    trash: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6l-1 14H6L5 6" />
        <path d="M10 11v6M14 11v6" />
        <path d="M9 6V4h6v2" />
      </svg>
    ),
    eye: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    ),
    plus: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
      </svg>
    ),
    pulse: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
    clipboard: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
        <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
      </svg>
    ),
    users: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
    log: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/>
        <polyline points="10 9 9 9 8 9"/>
      </svg>
    ),
    key: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
      </svg>
    ),
    toggle: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="5" width="22" height="14" rx="7" ry="7"/>
        <circle cx="8" cy="12" r="3"/>
      </svg>
    ),
    filter: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
      </svg>
    ),
  };
  return icons[name] || null;
};

// ============================================================
// STYLES
// ============================================================
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Figtree:wght@300;400;500;600;700;800&family=DM+Mono:wght@400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --navy: #FFFFFF;
    --navy-mid: #F5F7FA;
    --navy-light: #EEF1F5;
    --teal: #003366;
    --teal-dim: #002244;
    --teal-glow: rgba(0,51,102,0.07);
    --bc-teal: #0E7490;
    --bc-teal-light: #22D3EE;
    --bc-teal-soft: rgba(14,116,144,0.08);
    --amber: #D97706;
    --red: #DC2626;
    --white: #111827;
    --muted: #6B7280;
    --border: #E5E7EB;
    --card: #FFFFFF;
    --font: 'Figtree', 'Inter', ui-sans-serif, system-ui, sans-serif;
    --mono: 'DM Mono', monospace;
    --radius: 16px;
    --radius-sm: 10px;
    --shadow-sm: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);
    --shadow-md: 0 4px 16px rgba(0,51,102,0.09), 0 1px 4px rgba(0,0,0,0.05);
    --shadow-lg: 0 12px 40px rgba(0,51,102,0.14), 0 2px 8px rgba(0,0,0,0.06);
  }

  body { font-family: var(--font); background: var(--navy); color: var(--white); min-height: 100vh; }
  .skip-link {
    position: absolute; top: -100%; left: 12px; z-index: 999;
    background: var(--teal); color: #fff; font-size: 0.85rem; font-weight: 700;
    padding: 8px 16px; border-radius: 0 0 8px 8px; text-decoration: none;
    transition: top 0.15s;
  }
  .skip-link:focus { top: 0; }

  /* ---- LAYOUT ---- */
  .app { display: flex; min-height: 100vh; flex-direction: column; }
  .main-layout { display: flex; flex: 1; }
  .sidebar {
    width: 260px; min-height: calc(100vh - 64px);
    background: linear-gradient(180deg, #FAFBFC 0%, #F0F4F8 60%, #EAF0F5 100%);
    border-right: 1px solid var(--border); display: flex; flex-direction: column;
    padding: 16px 12px 0; gap: 2px; position: sticky; top: 64px; flex-shrink: 0;
    overflow: hidden;
  }
  .sidebar-scene {
    margin-top: auto; margin-left: -12px; margin-right: -12px;
    height: 110px; position: relative; overflow: hidden; flex-shrink: 0;
  }
  .sidebar-scene-label {
    position: absolute; bottom: 8px; left: 14px;
    font-size: 0.58rem; font-weight: 700; letter-spacing: 0.13em;
    text-transform: uppercase; color: rgba(0,51,102,0.55);
  }
  .content { flex: 1; padding: 32px; max-width: 980px; }

  /* ---- TOPBAR ---- */
  .topbar {
    height: 64px; background: #FFFFFF; border-bottom: 1px solid var(--border);
    display: flex; align-items: center; justify-content: space-between;
    padding: 0 24px; position: sticky; top: 0; z-index: 100;
    box-shadow: var(--shadow-sm);
  }
  .logo { display: flex; align-items: center; gap: 10px; text-decoration: none; }
  .logo-badge {
    width: 36px; height: 36px; border-radius: 10px; background: var(--teal);
    display: flex; align-items: center; justify-content: center;
    font-size: 0.72rem; font-weight: 800; color: #fff; letter-spacing: 0.02em;
  }
  .logo-text { display: flex; flex-direction: column; line-height: 1; }
  .logo-name { font-size: 1.05rem; font-weight: 800; color: var(--teal); letter-spacing: -0.01em; }
  .logo-sub { font-size: 0.6rem; font-weight: 700; color: var(--bc-teal); letter-spacing: 0.14em; text-transform: uppercase; margin-top: 2px; }
  .topbar-right { display: flex; align-items: center; gap: 12px; }
  .badge-pill {
    background: var(--bc-teal-soft); border: 1px solid rgba(14,116,144,0.25); color: var(--bc-teal);
    font-size: 0.68rem; font-weight: 700; letter-spacing: 0.07em; padding: 4px 10px;
    border-radius: 99px; text-transform: uppercase;
  }
  .avatar {
    width: 36px; height: 36px; border-radius: 50%; background: var(--teal);
    display: flex; align-items: center; justify-content: center; font-weight: 700;
    color: #fff; font-size: 0.82rem; cursor: pointer; flex-shrink: 0;
    box-shadow: 0 2px 8px rgba(0,51,102,0.2);
  }

  /* ---- NAV ---- */
  .nav-item {
    display: flex; align-items: center; gap: 10px; padding: 9px 12px;
    border-radius: var(--radius-sm); cursor: pointer; font-size: 0.875rem;
    font-weight: 500; color: var(--muted); transition: all 0.18s; border: none;
    background: none; width: 100%; text-align: left; position: relative;
  }
  .nav-item:hover { background: rgba(0,51,102,0.06); color: var(--teal); }
  .nav-item:focus-visible { outline: 2px solid var(--teal); outline-offset: -2px; background: rgba(0,51,102,0.06); }
  .nav-item.active {
    background: linear-gradient(90deg, rgba(0,51,102,0.1) 0%, rgba(0,51,102,0.04) 100%);
    color: var(--teal); font-weight: 700;
    box-shadow: inset 3px 0 0 var(--teal);
  }
  .nav-section {
    font-size: 0.6rem; letter-spacing: 0.14em; text-transform: uppercase;
    color: var(--muted); padding: 16px 12px 6px; font-weight: 800; opacity: 0.7;
  }
  .nav-divider { height: 1px; background: var(--border); margin: 8px 4px; }
  .logout-btn { color: var(--red) !important; }
  .logout-btn:hover { background: rgba(220,38,38,0.07) !important; }

  /* ---- AUTH PAGES ---- */
  .auth-screen {
    min-height: 100vh; display: flex; align-items: center; justify-content: center;
    padding: 24px 24px 60px; position: relative; overflow-x: hidden; overflow-y: auto;
    background: #001828;
  }
  .auth-bg-image {
    position: fixed; inset: -2%;
    background-image: url('/images/condor-wei.jpg');
    background-size: cover; background-position: center 40%;
    filter: blur(4px); opacity: 0.78;
  }
  .auth-bg-overlay {
    position: fixed; inset: 0;
    background: linear-gradient(135deg, rgba(0,20,40,0.58) 0%, rgba(0,51,102,0.38) 50%, rgba(14,116,144,0.22) 100%);
  }
  .auth-bc-label {
    color: var(--muted); font-size: 0.7rem; font-weight: 600;
    letter-spacing: 0.14em; text-transform: uppercase; text-align: center;
    white-space: nowrap; margin-top: 20px;
  }
  .auth-card {
    position: relative; z-index: 1;
    background: rgba(255,255,255,0.98); border: 1px solid rgba(255,255,255,0.6);
    border-radius: 24px; padding: 44px 40px; width: 100%; max-width: 440px;
    box-shadow: 0 32px 80px rgba(0,20,50,0.4), 0 0 0 1px rgba(255,255,255,0.2);
    backdrop-filter: blur(24px);
  }
  .auth-logo { display: flex; align-items: center; gap: 10px; margin-bottom: 6px; }
  .auth-logo-badge {
    width: 38px; height: 38px; border-radius: 10px; background: var(--teal);
    display: flex; align-items: center; justify-content: center;
    font-size: 0.7rem; font-weight: 800; color: #fff; letter-spacing: 0.02em;
  }
  .auth-logo-text { display: flex; flex-direction: column; line-height: 1; }
  .auth-logo-name { font-size: 1.15rem; font-weight: 800; color: var(--teal); letter-spacing: -0.01em; }
  .auth-logo-sub { font-size: 0.6rem; font-weight: 700; color: var(--bc-teal); letter-spacing: 0.14em; text-transform: uppercase; margin-top: 2px; }
  .auth-sub { color: var(--muted); font-size: 0.875rem; margin-bottom: 28px; margin-top: 4px; }
  .auth-tabs {
    display: flex; gap: 3px; background: var(--navy-mid); border-radius: var(--radius-sm);
    padding: 3px; margin-bottom: 24px;
  }
  .auth-tab {
    flex: 1; padding: 8px 6px; border-radius: 8px; cursor: pointer; font-size: 0.84rem;
    font-weight: 600; text-align: center; transition: all 0.15s; border: none;
    background: none; color: var(--muted); font-family: var(--font);
  }
  .auth-tab.active { background: var(--teal); color: #fff; box-shadow: 0 2px 8px rgba(0,51,102,0.25); }
  .auth-tab:focus-visible { outline: 2px solid var(--teal); outline-offset: 2px; }

  /* ---- FORMS ---- */
  .form-group { margin-bottom: 16px; }
  .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  label {
    display: block; font-size: 0.75rem; font-weight: 700; color: var(--muted);
    margin-bottom: 6px; letter-spacing: 0.05em; text-transform: uppercase;
  }
  input, select, textarea {
    width: 100%; background: var(--navy); border: 1.5px solid var(--border);
    border-radius: var(--radius-sm); color: var(--white); font-family: var(--font);
    font-size: 0.9rem; padding: 11px 14px; outline: none; transition: border-color 0.15s, box-shadow 0.15s;
  }
  input:focus, select:focus, textarea:focus {
    border-color: var(--teal); box-shadow: 0 0 0 3px rgba(0,51,102,0.08);
  }
  input.input-error, select.input-error, textarea.input-error {
    border-color: var(--red); box-shadow: 0 0 0 3px rgba(220,38,38,0.10);
  }
  input.input-error:focus, select.input-error:focus, textarea.input-error:focus {
    border-color: var(--red); box-shadow: 0 0 0 3px rgba(220,38,38,0.18);
  }
  input::placeholder, textarea::placeholder { color: #9CA3AF; }
  textarea { resize: vertical; min-height: 90px; }
  select option { background: var(--navy-mid); }

  /* ---- BUTTONS ---- */
  .btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    padding: 11px 22px; border-radius: var(--radius-sm); font-family: var(--font);
    font-size: 0.88rem; font-weight: 600; cursor: pointer; transition: all 0.15s;
    border: none; white-space: nowrap;
  }
  .btn-primary {
    background: var(--teal); color: #fff;
    box-shadow: 0 2px 8px rgba(0,51,102,0.2);
  }
  .btn-primary:hover {
    background: var(--teal-dim); transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(0,51,102,0.28);
  }
  .btn-outline { background: transparent; color: var(--teal); border: 1.5px solid var(--teal); }
  .btn-outline:hover { background: var(--teal-glow); }
  .btn-ghost { background: transparent; color: var(--muted); border: 1.5px solid var(--border); }
  .btn-ghost:hover { border-color: var(--teal); color: var(--teal); }
  .btn-danger { background: rgba(220,38,38,0.08); color: var(--red); border: 1.5px solid rgba(220,38,38,0.25); }
  .btn-danger:hover { background: rgba(220,38,38,0.15); }
  .btn-sm { padding: 7px 14px; font-size: 0.8rem; }
  .btn-full { width: 100%; }
  .btn:disabled { opacity: 0.45; cursor: not-allowed; transform: none !important; box-shadow: none !important; }
  .btn:focus-visible { outline: 2px solid var(--teal); outline-offset: 2px; }
  .btn-interest { background: var(--teal-glow); color: var(--teal); border: 1.5px solid rgba(0,51,102,0.3); }
  .btn-interest.expressed { background: var(--teal); color: #fff; box-shadow: 0 2px 8px rgba(0,51,102,0.2); }

  /* ---- CARDS ---- */
  .card {
    background: var(--card); border: 1px solid var(--border); border-radius: var(--radius);
    padding: 24px; box-shadow: var(--shadow-sm);
  }
  .card-sm { padding: 16px; }
  .card + .card { margin-top: 14px; }

  /* ---- CONTRACT BOARD ---- */
  .contracts-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 18px; }
  .contract-card {
    background: var(--card); border: 1px solid var(--border); border-radius: var(--radius);
    padding: 22px; cursor: pointer; transition: all 0.2s;
    display: flex; flex-direction: column; gap: 12px;
    position: relative; overflow: hidden;
    box-shadow: var(--shadow-sm);
  }
  .contract-card::before {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px;
    background: linear-gradient(90deg, var(--teal) 0%, var(--bc-teal) 100%);
  }
  .contract-card:hover {
    border-color: rgba(0,51,102,0.3); transform: translateY(-3px);
    box-shadow: var(--shadow-lg);
  }
  .contract-card.urgent::before {
    background: linear-gradient(90deg, var(--amber) 0%, #F59E0B 100%);
  }
  .contract-title { font-size: 1rem; font-weight: 700; color: var(--white); line-height: 1.3; }
  .contract-meta { display: flex; flex-wrap: wrap; gap: 6px; }
  .meta-chip {
    display: flex; align-items: center; gap: 4px; font-size: 0.73rem; color: var(--muted);
    background: var(--navy-mid); border: 1px solid var(--border); border-radius: 99px; padding: 3px 10px;
  }
  .tag { font-size: 0.68rem; font-weight: 700; letter-spacing: 0.06em; padding: 3px 9px; border-radius: 99px; }
  .tag-specialty { background: rgba(0,51,102,0.07); color: var(--teal); border: 1px solid rgba(0,51,102,0.16); }
  .tag-urgent { background: rgba(217,119,6,0.08); color: var(--amber); border: 1px solid rgba(217,119,6,0.2); }
  .tag-new { background: var(--bc-teal-soft); color: var(--bc-teal); border: 1px solid rgba(14,116,144,0.2); }
  .contract-desc { font-size: 0.83rem; color: var(--muted); line-height: 1.65; }
  .contract-footer { margin-top: auto; display: flex; align-items: center; justify-content: space-between; }
  .pay-rate { font-family: var(--mono); font-size: 1.05rem; font-weight: 700; color: var(--teal); }

  /* ---- MODAL ---- */
  .modal-overlay {
    position: fixed; inset: 0; background: rgba(0,20,50,0.5); z-index: 200;
    display: flex; align-items: center; justify-content: center; padding: 24px;
    backdrop-filter: blur(6px);
  }
  .modal {
    background: #FFFFFF; border: 1px solid var(--border); border-radius: 24px;
    width: 100%; max-width: 600px; max-height: 90vh; overflow-y: auto;
    padding: 32px; position: relative; box-shadow: var(--shadow-lg);
  }
  .modal-close {
    position: absolute; top: 16px; right: 16px; background: var(--navy-mid);
    border: 1px solid var(--border); border-radius: 8px; color: var(--muted);
    cursor: pointer; padding: 6px; line-height: 0; transition: all 0.15s;
  }
  .modal-close:hover { background: var(--border); color: var(--white); }
  .modal-close:focus-visible { outline: 2px solid var(--teal); outline-offset: 2px; }
  .modal-title { font-size: 1.3rem; font-weight: 800; margin-bottom: 6px; color: var(--white); }
  .modal-section { margin-top: 20px; }
  .modal-section h4 {
    font-size: 0.72rem; letter-spacing: 0.1em; text-transform: uppercase;
    color: var(--muted); margin-bottom: 10px; font-weight: 700;
  }
  .req-list { list-style: none; display: flex; flex-direction: column; gap: 6px; }
  .req-list li { display: flex; align-items: center; gap: 8px; font-size: 0.85rem; color: var(--white); }

  /* ---- DASHBOARD STATS ---- */
  .stats-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 14px; margin-bottom: 28px; }
  .stat-card {
    background: var(--card); border: 1px solid var(--border); border-radius: var(--radius);
    padding: 20px 22px; display: flex; flex-direction: column; gap: 6px;
    box-shadow: var(--shadow-sm); transition: box-shadow 0.15s, transform 0.15s;
  }
  .stat-card:hover { box-shadow: var(--shadow-md); transform: translateY(-2px); }
  .stat-label { font-size: 0.72rem; letter-spacing: 0.08em; text-transform: uppercase; color: var(--muted); font-weight: 700; }
  .stat-value { font-size: 2rem; font-weight: 800; color: var(--teal); font-family: var(--mono); line-height: 1; }
  .stat-icon { color: var(--bc-teal); margin-bottom: 2px; }

  /* ---- UPLOAD ZONE ---- */
  .upload-zone {
    border: 2px dashed var(--border); border-radius: var(--radius); padding: 36px;
    text-align: center; cursor: pointer; transition: all 0.22s;
    background: rgba(0,51,102,0.02);
  }
  .upload-zone:hover, .upload-zone.drag-over { border-color: var(--teal); background: var(--teal-glow); }
  .upload-zone-icon { color: var(--teal); margin-bottom: 12px; }
  .upload-zone p { color: var(--muted); font-size: 0.88rem; }
  .upload-zone strong { color: var(--white); }
  .file-list { display: flex; flex-direction: column; gap: 10px; margin-top: 16px; }
  .file-item {
    display: flex; align-items: center; gap: 12px; background: var(--navy-mid);
    border: 1px solid var(--border); border-radius: var(--radius-sm); padding: 12px 14px;
  }
  .file-icon { color: var(--teal); flex-shrink: 0; }
  .file-info { flex: 1; min-width: 0; }
  .file-name { font-size: 0.85rem; font-weight: 600; truncate: clip; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .file-meta { font-size: 0.75rem; color: var(--muted); font-family: var(--mono); }
  .file-status { width: 22px; height: 22px; border-radius: 50%; background: var(--teal); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }

  /* ---- ADMIN TABLE ---- */
  .table-wrap { overflow-x: auto; border-radius: var(--radius); border: 1px solid var(--border); box-shadow: var(--shadow-sm); }
  table { width: 100%; border-collapse: collapse; font-size: 0.85rem; background: var(--card); }
  thead { background: var(--navy-mid); }
  th {
    text-align: left; padding: 11px 16px; font-size: 0.7rem; letter-spacing: 0.08em;
    text-transform: uppercase; color: var(--muted); border-bottom: 1px solid var(--border); font-weight: 700;
  }
  td { padding: 13px 16px; border-bottom: 1px solid var(--border); vertical-align: middle; }
  tbody tr:last-child td { border-bottom: none; }
  tbody tr:hover td { background: rgba(0,51,102,0.025); }

  /* ---- PAGE HEADER ---- */
  .page-header { margin-bottom: 28px; }
  .page-title { font-size: 1.55rem; font-weight: 800; letter-spacing: -0.02em; color: var(--white); }
  .page-sub { color: var(--muted); font-size: 0.88rem; margin-top: 5px; }

  /* ---- ALERTS ---- */
  .alert { border-radius: var(--radius-sm); padding: 12px 16px; font-size: 0.85rem; margin-bottom: 16px; font-weight: 500; }
  .alert-success { background: rgba(14,116,144,0.07); border: 1px solid rgba(14,116,144,0.22); color: var(--bc-teal); }
  .alert-error { background: rgba(220,38,38,0.07); border: 1px solid rgba(220,38,38,0.22); color: var(--red); }
  .alert-info { background: rgba(217,119,6,0.07); border: 1px solid rgba(217,119,6,0.22); color: var(--amber); }

  /* ---- MISC ---- */
  .text-muted { color: var(--muted); }
  .text-teal { color: var(--teal); }
  .text-bc-teal { color: var(--bc-teal); }
  .text-sm { font-size: 0.82rem; }
  .text-mono { font-family: var(--mono); }
  .flex { display: flex; }
  .items-center { align-items: center; }
  .justify-between { justify-content: space-between; }
  .gap-8 { gap: 8px; }
  .gap-12 { gap: 12px; }
  .gap-16 { gap: 16px; }
  .mb-4 { margin-bottom: 4px; }
  .mb-8 { margin-bottom: 8px; }
  .mb-16 { margin-bottom: 16px; }
  .mb-24 { margin-bottom: 24px; }
  .mt-16 { margin-top: 16px; }
  .mt-24 { margin-top: 24px; }
  .fw-700 { font-weight: 700; }
  .security-bar {
    background: rgba(14,116,144,0.04); border-bottom: 1px solid var(--border);
    padding: 6px 24px; display: flex; align-items: center; gap: 10px;
    font-size: 0.7rem; color: var(--muted); letter-spacing: 0.04em; font-weight: 500;
  }
  .security-dot {
    width: 7px; height: 7px; border-radius: 50%; background: var(--bc-teal);
    animation: pulse-dot 2.5s infinite; flex-shrink: 0;
  }
  @keyframes pulse-dot { 0%,100%{opacity:1} 50%{opacity:0.35} }
  .interest-count { font-family: var(--mono); font-size: 0.78rem; color: var(--muted); }

  /* ---- AVAILABILITY LIST ---- */
  .avail-row {
    display: flex; align-items: center; gap: 14px; padding: 12px 16px;
    border-bottom: 1px solid var(--border); transition: background 0.12s;
  }
  .avail-row:last-child { border-bottom: none; }
  .avail-row:hover { background: rgba(0,51,102,0.025); }
  .avail-dot {
    width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0;
    transition: background 0.2s;
  }
  .avail-dot.open { background: #10B981; box-shadow: 0 0 0 3px rgba(16,185,129,0.2); }
  .avail-dot.closed { background: var(--border); }

  /* ---- INTEREST NURSE ROW ---- */
  .interest-nurse-row {
    display: flex; align-items: center; gap: 12px; padding: 10px 14px;
    border-radius: var(--radius-sm); margin-bottom: 6px;
    background: var(--navy-mid); border: 1px solid var(--border);
    transition: all 0.15s;
  }
  .interest-nurse-row:hover { border-color: rgba(0,51,102,0.2); }

  /* ---- NURSE PROFILE PANEL ---- */
  .nurse-panel { max-width: 700px; }
  .designation-badge {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 5px 14px; border-radius: 99px; font-size: 0.75rem;
    font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase;
  }
  .designation-inc { background: rgba(14,116,144,0.1); color: var(--bc-teal); border: 1.5px solid rgba(14,116,144,0.3); }
  .designation-staff { background: rgba(0,51,102,0.08); color: var(--teal); border: 1.5px solid rgba(0,51,102,0.25); }
  .exp-bar {
    height: 6px; border-radius: 3px; background: var(--border); overflow: hidden; margin-top: 6px;
  }
  .exp-bar-fill { height: 100%; border-radius: 3px; background: linear-gradient(90deg, var(--teal) 0%, var(--bc-teal) 100%); transition: width 0.5s; }
  .search-box {
    position: relative; margin-bottom: 20px;
  }
  .search-box input {
    padding-left: 40px; background: var(--navy-mid);
  }
  .search-box-icon {
    position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--muted);
  }
  .profile-tab-bar {
    display: flex; gap: 2px; border-bottom: 2px solid var(--border); margin-bottom: 20px;
  }
  .profile-tab {
    padding: 8px 16px; font-size: 0.82rem; font-weight: 600; color: var(--muted);
    cursor: pointer; border: none; background: none; border-bottom: 2px solid transparent;
    margin-bottom: -2px; transition: all 0.15s; font-family: var(--font);
  }
  .profile-tab.active { color: var(--teal); border-bottom-color: var(--teal); }
  .profile-tab:hover:not(.active) { color: var(--white); }
  .profile-tab:focus-visible { outline: 2px solid var(--teal); outline-offset: -2px; }
  .empty-state { text-align: center; padding: 60px 24px; color: var(--muted); }
  .empty-state-icon { color: var(--border); margin-bottom: 16px; }
  /* ---- MOBILE NAV ---- */
  .topbar-hamburger {
    display: none; align-items: center; justify-content: center;
    width: 36px; height: 36px; border-radius: 8px; background: none;
    border: 1.5px solid var(--border); cursor: pointer; color: var(--muted);
    transition: all 0.15s; flex-shrink: 0;
  }
  .topbar-hamburger:hover { background: var(--teal-glow); color: var(--teal); border-color: rgba(0,51,102,0.3); }
  .topbar-hamburger:focus-visible { outline: 2px solid var(--teal); outline-offset: 2px; }
  .mobile-nav-overlay {
    position: fixed; inset: 0; background: rgba(0,20,50,0.45); z-index: 150;
    backdrop-filter: blur(4px); animation: fadeIn 0.18s ease;
  }
  .mobile-nav-drawer {
    position: fixed; left: 0; top: 0; bottom: 0; width: 272px;
    background: linear-gradient(180deg, #FAFBFC 0%, #F0F4F8 60%, #EAF0F5 100%);
    border-right: 1px solid var(--border); z-index: 151;
    display: flex; flex-direction: column; overflow: hidden;
    animation: slideInLeft 0.22s cubic-bezier(0.22,1,0.36,1);
    box-shadow: 4px 0 24px rgba(0,51,102,0.12);
  }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes slideInLeft { from { transform: translateX(-100%); } to { transform: translateX(0); } }
  .mobile-nav-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 14px 16px; border-bottom: 1px solid var(--border); flex-shrink: 0;
  }
  .mobile-nav-close {
    width: 32px; height: 32px; border-radius: 8px; border: 1px solid var(--border);
    background: none; cursor: pointer; color: var(--muted); display: flex;
    align-items: center; justify-content: center; transition: all 0.15s;
  }
  .mobile-nav-close:hover { background: var(--border); color: var(--white); }
  .mobile-nav-body { padding: 12px; display: flex; flex-direction: column; gap: 2px; flex: 1; overflow-y: auto; }
  .mobile-nav-footer { padding: 12px; border-top: 1px solid var(--border); flex-shrink: 0; }
  .mobile-nav-scene {
    height: 90px; position: relative; overflow: hidden; flex-shrink: 0;
    margin: 0 -12px; /* bleed to edges of body padding */
  }

  @media (max-width: 768px) {
    .sidebar { display: none; }
    .topbar-hamburger { display: flex; }
    .badge-pill { display: none; }
    .content { padding: 20px 16px; }
    .form-row { grid-template-columns: 1fr; }
    .contracts-grid { grid-template-columns: 1fr; }
    .auth-card { padding: 32px 24px; }
    .stats-row { grid-template-columns: repeat(2, 1fr); }
    .topbar { padding: 0 16px; }
    .security-bar { display: none; }
  }
`;

// ============================================================
// RESET PASSWORD VIEW (shown when ?reset=TOKEN is in the URL)
// ============================================================
function ResetPasswordView({ token, onDone }) {
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [pwFocused, setPwFocused] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    if (!done) return;
    if (countdown <= 0) { onDone(); return; }
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [done, countdown]);

  const handleSubmit = async () => {
    setError("");
    if (!form.password || !form.confirm) { setError("Please fill in both fields."); return; }
    if (form.password !== form.confirm) { setError("Passwords do not match."); return; }
    const pwOk = form.password.length >= 12 && /[A-Z]/.test(form.password) && /[a-z]/.test(form.password) && /[0-9]/.test(form.password) && /[^A-Za-z0-9]/.test(form.password);
    if (!pwOk) { setError("Password must be at least 12 characters and include uppercase, lowercase, a number, and a symbol."); return; }
    setBusy(true);
    try {
      await Auth.resetPassword(token, form.password);
      setDone(true);
    } catch (e) {
      setError(e.message || "Something went wrong. This link may have expired.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-screen">
      <div className="auth-bg-image" aria-hidden="true" />
      <div className="auth-bg-overlay" aria-hidden="true" />
      <div className="auth-card">
        <div className="auth-logo">
          <div className="auth-logo-badge">NLC</div>
          <div className="auth-logo-text">
            <span className="auth-logo-name">Portal</span>
            <span className="auth-logo-sub">British Columbia</span>
          </div>
        </div>
        {done ? (
          <>
            <div className="alert alert-success" style={{ marginTop: 8 }}>Your password has been updated. Redirecting to sign in in {countdown}…</div>
            <button className="btn btn-primary btn-full" style={{ marginTop: 8 }} onClick={onDone}>Sign In Now</button>
          </>
        ) : (
          <>
            <p style={{ fontSize: "0.9rem", color: "var(--muted)", margin: "4px 0 20px" }}>Choose a new password for your account.</p>
            <div className="form-group">
              <label>New Password</label>
              <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} onFocus={() => setPwFocused(true)} onBlur={() => setPwFocused(false)} />
              {(pwFocused || form.password.length > 0) && (
                <ul style={{ margin: "6px 0 0", padding: "0 0 0 16px", fontSize: "0.78rem", color: "var(--muted)", lineHeight: 1.7 }}>
                  <li style={{ color: form.password.length >= 12 ? "var(--teal)" : "inherit" }}>At least 12 characters</li>
                  <li style={{ color: /[A-Z]/.test(form.password) ? "var(--teal)" : "inherit" }}>Uppercase letter (A–Z)</li>
                  <li style={{ color: /[a-z]/.test(form.password) ? "var(--teal)" : "inherit" }}>Lowercase letter (a–z)</li>
                  <li style={{ color: /[0-9]/.test(form.password) ? "var(--teal)" : "inherit" }}>Number (0–9)</li>
                  <li style={{ color: /[^A-Za-z0-9]/.test(form.password) ? "var(--teal)" : "inherit" }}>Symbol (e.g. !@#$%)</li>
                </ul>
              )}
            </div>
            <div className="form-group">
              <label>Confirm New Password</label>
              <input type="password" value={form.confirm} onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} onKeyDown={e => e.key === "Enter" && !busy && handleSubmit()} />
            </div>
            {error && <div className="alert alert-error" style={{ marginBottom: 8 }}>{error}</div>}
            <button className="btn btn-primary btn-full" style={{ marginTop: 8 }} onClick={handleSubmit} disabled={busy}>
              {busy ? "Saving…" : "Set New Password"}
            </button>
            <button style={{ background: "none", border: "none", color: "var(--muted)", fontSize: "0.82rem", cursor: "pointer", marginTop: 12, width: "100%", textAlign: "center" }} onClick={onDone}>
              Back to Sign In
            </button>
          </>
        )}
        <p className="auth-bc-label">British Columbia · Canada</p>
      </div>
    </div>
  );
}

function ForgotPasswordForm({ onBack }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    setError(""); setBusy(true);
    try { await Auth.forgotPassword(email); setDone(true); }
    catch (e) { setError(e.message || "Something went wrong."); }
    finally { setBusy(false); }
  };

  if (done) return (
    <>
      <div className="alert alert-success">Check your email — if an account exists for that address, a reset link is on its way.</div>
      <button style={{ background: "none", border: "none", color: "var(--muted)", fontSize: "0.82rem", cursor: "pointer", marginTop: 8, width: "100%", textAlign: "center" }} onClick={onBack}>Back to Sign In</button>
    </>
  );

  return (
    <>
      <p style={{ fontSize: "0.88rem", color: "var(--muted)", marginBottom: 16 }}>Enter your email and we'll send you a link to reset your password.</p>
      <div className="form-group">
        <label>Email Address</label>
        <input type="email" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && !busy && handleSubmit()} />
      </div>
      {error && <div className="alert alert-error" style={{ marginBottom: 8 }}>{error}</div>}
      <button className="btn btn-primary btn-full" style={{ marginTop: 8 }} onClick={handleSubmit} disabled={busy || !email}>
        {busy ? "Sending…" : "Send Reset Link"}
      </button>
      <button style={{ background: "none", border: "none", color: "var(--muted)", fontSize: "0.82rem", cursor: "pointer", marginTop: 12, width: "100%", textAlign: "center" }} onClick={onBack}>Back to Sign In</button>
    </>
  );
}

// ============================================================
// AUTH SCREEN
// ============================================================
function AuthScreen({ onLogin }) {
  const [tab, setTab] = useState("login");
  const [role, setRole] = useState("nurse");
  const [form, setForm] = useState({
    email: "", password: "", confirmPassword: "", firstName: "", lastName: "",
    phone: "", accessCode: "",
  });
  const [pwFocused, setPwFocused] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [busy, setBusy] = useState(false);
  const [attempted, setAttempted] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleLogin = async () => {
    setError(""); setSuccess(""); setAttempted(true); setBusy(true);
    try {
      const data = await Auth.login(form.email, form.password);
      if (role === "admin" && data.user.role !== "admin") {
        clearTokens();
        setError("These credentials do not belong to an admin account.");
        return;
      }
      onLogin(data.user);
    } catch (e) {
      setError("Wrong email or password.");
    } finally {
      setBusy(false);
    }
  };

  const handleRegister = async () => {
    setError(""); setSuccess(""); setAttempted(true);
    if (!form.firstName || !form.lastName || !form.email || !form.password) {
      setError("Please fill in all required fields."); return;
    }
    if (!form.accessCode.trim()) { setError("An access code is required to create an account."); return; }
    if (form.password !== form.confirmPassword) { setError("Passwords do not match."); return; }
    const pwOk = form.password.length >= 12 && /[A-Z]/.test(form.password) && /[a-z]/.test(form.password) && /[0-9]/.test(form.password) && /[^A-Za-z0-9]/.test(form.password);
    if (!pwOk) { setError("Password must be at least 12 characters and include uppercase, lowercase, a number, and a symbol."); return; }
    setBusy(true);
    try {
      await Auth.register({
        email: form.email,
        password: form.password,
        firstName: form.firstName,
        lastName: form.lastName,
        phone: form.phone,
        accessCode: form.accessCode.trim().toUpperCase(),
      });
      setSuccess("Account created! You can now log in.");
      setTab("login");
      setAttempted(false);
      setForm(f => ({ ...f, password: "", confirmPassword: "", accessCode: "" }));
    } catch (e) {
      setError(e.message || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-screen">
      <div className="auth-bg-image" aria-hidden="true" />
      <div className="auth-bg-overlay" aria-hidden="true" />
      <div className="auth-card">
        <div className="auth-logo">
          <div className="auth-logo-badge">NLC</div>
          <div className="auth-logo-text">
            <span className="auth-logo-name">Portal</span>
            <span className="auth-logo-sub">British Columbia</span>
          </div>
        </div>

        {/* Role Selector */}
        <div className="auth-tabs" style={{ marginBottom: 12 }} role="tablist" aria-label="Select role">
          <button role="tab" aria-selected={role === "nurse"} className={`auth-tab ${role === "nurse" ? "active" : ""}`} onClick={() => { setRole("nurse"); setTab("login"); setError(""); setAttempted(false); }}>Employee</button>
          <button role="tab" aria-selected={role === "admin"} className={`auth-tab ${role === "admin" ? "active" : ""}`} onClick={() => { setRole("admin"); setTab("login"); setError(""); setAttempted(false); }}>Staff</button>
        </div>

        {/* Login / Register tabs (nurses only) */}
        {role === "nurse" && (
          <div className="auth-tabs" role="tablist" aria-label="Sign in or create account">
            <button role="tab" aria-selected={tab === "login"} className={`auth-tab ${tab === "login" ? "active" : ""}`} onClick={() => { setTab("login"); setError(""); setAttempted(false); }}>Sign In</button>
            <button role="tab" aria-selected={tab === "register"} className={`auth-tab ${tab === "register" ? "active" : ""}`} onClick={() => { setTab("register"); setError(""); setAttempted(false); }}>Create Account</button>
          </div>
        )}

        {success && <div className="alert alert-success">{success}</div>}

        {/* FORGOT PASSWORD */}
        {tab === "forgot" && (
          <ForgotPasswordForm onBack={() => { setTab("login"); setError(""); setAttempted(false); }} />
        )}

        {/* LOGIN */}
        {(tab === "login" || role === "admin") && (
          <>
            <div className="form-group">
              <label>Email Address</label>
              <input type="email" placeholder="" value={form.email} onChange={e => { set("email", e.target.value); setAttempted(false); setError(""); }} className={attempted && error ? "input-error" : ""} />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input type="password" placeholder="" value={form.password} onChange={e => { set("password", e.target.value); setAttempted(false); setError(""); }} onKeyDown={e => e.key === "Enter" && !busy && handleLogin()} className={attempted && error ? "input-error" : ""} />
            </div>
            {error && <div className="alert alert-error" style={{ marginBottom: 8 }}>{error}</div>}
            <button className="btn btn-primary btn-full" style={{ marginTop: 8 }} onClick={handleLogin} disabled={busy}>
              {busy ? "Signing in..." : "Sign In"}
            </button>
            <button style={{ background: "none", border: "none", color: "var(--muted)", fontSize: "0.82rem", cursor: "pointer", marginTop: 10, width: "100%", textAlign: "center" }} onClick={() => { setTab("forgot"); setError(""); setAttempted(false); }}>
              Forgot your password?
            </button>
          </>
        )}

        {/* REGISTER */}
        {tab === "register" && role === "nurse" && (() => {
          const pwOk = form.password.length >= 12 && /[A-Z]/.test(form.password) && /[a-z]/.test(form.password) && /[0-9]/.test(form.password) && /[^A-Za-z0-9]/.test(form.password);
          const firstErr = attempted && !form.firstName;
          const lastErr = attempted && !form.lastName;
          const emailErr = attempted && !form.email;
          const pwErr = attempted && (!form.password || !pwOk);
          const confirmErr = attempted && form.password !== form.confirmPassword;
          return (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input placeholder="" value={form.firstName} onChange={e => set("firstName", e.target.value)} className={firstErr ? "input-error" : ""} />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input placeholder="" value={form.lastName} onChange={e => set("lastName", e.target.value)} className={lastErr ? "input-error" : ""} />
                </div>
              </div>
              <div className="form-group">
                <label>Email Address *</label>
                <input type="email" placeholder="" value={form.email} onChange={e => set("email", e.target.value)} className={emailErr ? "input-error" : ""} />
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                <input placeholder="" value={form.phone} onChange={e => set("phone", e.target.value)} />
              </div>
              <div className="form-group">
                <label>Access Code *</label>
                <input
                  placeholder="e.g. NLC-ABCD1234"
                  value={form.accessCode}
                  onChange={e => { set("accessCode", e.target.value.toUpperCase()); setAttempted(false); setError(""); }}
                  className={attempted && !form.accessCode.trim() ? "input-error" : ""}
                  style={{ fontFamily: "monospace", letterSpacing: "0.06em" }}
                  autoComplete="off"
                />
                <p style={{ fontSize: "0.75rem", color: "var(--muted)", margin: "4px 0 0" }}>
                  You need an access code from NLC Staffing to create an account.
                </p>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Password *</label>
                  <input type="password" placeholder="" value={form.password} onChange={e => set("password", e.target.value)} onFocus={() => setPwFocused(true)} onBlur={() => setPwFocused(false)} className={pwErr ? "input-error" : ""} />
                  {(pwFocused || form.password.length > 0) && (
                    <ul style={{ margin: "6px 0 0", padding: "0 0 0 16px", fontSize: "0.78rem", color: "var(--muted)", lineHeight: 1.7 }}>
                      <li style={{ color: form.password.length >= 12 ? "var(--teal)" : "inherit" }}>At least 12 characters</li>
                      <li style={{ color: /[A-Z]/.test(form.password) ? "var(--teal)" : "inherit" }}>Uppercase letter (A–Z)</li>
                      <li style={{ color: /[a-z]/.test(form.password) ? "var(--teal)" : "inherit" }}>Lowercase letter (a–z)</li>
                      <li style={{ color: /[0-9]/.test(form.password) ? "var(--teal)" : "inherit" }}>Number (0–9)</li>
                      <li style={{ color: /[^A-Za-z0-9]/.test(form.password) ? "var(--teal)" : "inherit" }}>Symbol (e.g. !@#$%)</li>
                    </ul>
                  )}
                </div>
                <div className="form-group">
                  <label>Confirm Password *</label>
                  <input type="password" placeholder="" value={form.confirmPassword} onChange={e => set("confirmPassword", e.target.value)} className={confirmErr ? "input-error" : ""} />
                </div>
              </div>
              {error && <div className="alert alert-error" style={{ marginBottom: 8 }}>{error}</div>}
              <button className="btn btn-primary btn-full" style={{ marginTop: 8 }} onClick={handleRegister} disabled={busy}>
                {busy ? "Creating..." : "Create Account"}
              </button>
            </>
          );
        })()}

        <p className="auth-bc-label">British Columbia · Canada</p>
      </div>
    </div>
  );
}

// ============================================================
// NURSE: CONTRACT BOARD
// ============================================================
const BC_REGIONS = ["Fraser Valley","Metro Vancouver","Vancouver Island","Thompson-Okanagan","Northern BC","Kootenay","Cariboo","Northeast BC","Northwest BC"];
const SPECIALTIES = ["ICU","Emergency","OR","Pediatrics","Oncology","Med-Surg","Cardiac","Neurology","NICU","Obstetrics","Psychiatry","Home Care","Other"];
const CONTRACT_LENGTHS = ["1-4 weeks","5-8 weeks","9-13 weeks","14-26 weeks","26+ weeks"];

function ContractBoard({ user }) {
  const [contracts, setContracts] = useState([]);
  const [authorized, setAuthorized] = useState(false);
  const [interests, setInterests] = useState(new Set());
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  const [filterSpecialty, setFilterSpecialty] = useState("");
  const [filterLength, setFilterLength] = useState("");
  const [sortOrder, setSortOrder] = useState("soonest");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [c, i] = await Promise.all([Contracts.list(), Interests.mine()]);
        if (cancelled) return;
        setContracts(c.contracts || []);
        setAuthorized(c.authorized ?? (user?.role === "admin"));
        setInterests(new Set(i.contractIds || []));
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load contracts");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const expressed = (cid) => interests.has(cid);

  const express = async (contract) => {
    setInterests(prev => new Set(prev).add(contract.id));
    try {
      await Interests.create(contract.id);
    } catch (e) {
      setInterests(prev => { const n = new Set(prev); n.delete(contract.id); return n; });
      setError(e.message || "Failed to express interest");
    }
  };

  const remove = async (contract) => {
    setInterests(prev => { const n = new Set(prev); n.delete(contract.id); return n; });
    try {
      await Interests.remove(contract.id);
    } catch (e) {
      setInterests(prev => new Set(prev).add(contract.id));
      setError(e.message || "Failed to withdraw interest");
    }
  };

  const filtered = contracts
    .filter(c => {
      if (filter && !(c.specialty || "").toLowerCase().includes(filter.toLowerCase()) && !(c.location || "").toLowerCase().includes(filter.toLowerCase()) && !(c.title || "").toLowerCase().includes(filter.toLowerCase())) return false;
      if (filterLocation && !(c.location || "").toLowerCase().includes(filterLocation.toLowerCase())) return false;
      if (filterSpecialty && c.specialty !== filterSpecialty) return false;
      if (filterLength && c.duration !== filterLength) return false;
      return true;
    })
    .sort((a, b) => {
      const aInterested = interests.has(a.id) ? -2 : 0;
      const bInterested = interests.has(b.id) ? -2 : 0;
      const aUrgent = a.urgent ? -1 : 0;
      const bUrgent = b.urgent ? -1 : 0;
      const priority = (aInterested + aUrgent) - (bInterested + bUrgent);
      if (priority !== 0) return priority;
      if (sortOrder === "soonest") {
        const aDate = a.startDate ? new Date(a.startDate) : Infinity;
        const bDate = b.startDate ? new Date(b.startDate) : Infinity;
        return aDate - bDate;
      }
      const aDate = a.startDate ? new Date(a.startDate) : 0;
      const bDate = b.startDate ? new Date(b.startDate) : 0;
      return bDate - aDate;
    });
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div>
      <div className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Contract Board</h1>
            <p className="page-sub">Browse open contracts and express your interest — your recruiter will reach out to you</p>
          </div>
          <span className="badge-pill">{contracts.length} Active</span>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 20 }}>
          <input style={{ maxWidth: 240, flex: "0 0 auto" }} placeholder="Keyword search..." value={filter} onChange={e => { setFilter(e.target.value); setPage(1); }} />
          <select style={{ maxWidth: 180, fontSize: "0.84rem" }} value={filterLocation} onChange={e => { setFilterLocation(e.target.value); setPage(1); }}>
            <option value="">All BC regions</option>
            {BC_REGIONS.map(r => <option key={r}>{r}</option>)}
          </select>
          <select style={{ maxWidth: 160, fontSize: "0.84rem" }} value={filterSpecialty} onChange={e => { setFilterSpecialty(e.target.value); setPage(1); }}>
            <option value="">All specialties</option>
            {SPECIALTIES.map(s => <option key={s}>{s}</option>)}
          </select>
          <select style={{ maxWidth: 160, fontSize: "0.84rem" }} value={filterLength} onChange={e => { setFilterLength(e.target.value); setPage(1); }}>
            <option value="">Any length</option>
            {CONTRACT_LENGTHS.map(l => <option key={l}>{l}</option>)}
          </select>
          <select style={{ maxWidth: 160, fontSize: "0.84rem" }} value={sortOrder} onChange={e => { setSortOrder(e.target.value); setPage(1); }}>
            <option value="soonest">Start: Soonest first</option>
            <option value="latest">Start: Latest first</option>
          </select>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {!authorized && !loading && (
        <div className="alert alert-info" style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Icon name="lock" size={16} />
          <span>Your account is pending contract authorization. Your recruiter will review your documents and unlock full contract details. You can still browse titles and express interest.</span>
        </div>
      )}
      {loading && <p className="text-muted">Loading contracts…</p>}

      <div className="contracts-grid">
        {paginated.map(c => (
          <div key={c.id} className={`contract-card ${c.urgent ? "urgent" : ""}`} onClick={() => { setSelected(c); Contracts.recordView(c.id).catch(() => {}); }}>
            <div className="flex gap-8">
              {c.specialty && <span className="tag tag-specialty">{c.specialty}</span>}
              {c.urgent && <span className="tag tag-urgent">Urgent</span>}
            </div>
            <div className="contract-title">{c.title}</div>
            <div className="contract-meta">
              {c.location && <span className="meta-chip"><Icon name="map" size={12} />{c.location}</span>}
              {c.duration && <span className="meta-chip"><Icon name="clock" size={12} />{c.duration}</span>}
              {c.shift    && <span className="meta-chip"><Icon name="star" size={12} />{c.shift}</span>}
            </div>
            <p className="contract-desc">{(c.description || "").slice(0, 100)}...</p>
            <div className="contract-footer">
              <span className="pay-rate">{c.payRate}</span>
              <button
                className={`btn btn-sm btn-interest ${expressed(c.id) ? "expressed" : ""}`}
                onClick={e => { e.stopPropagation(); expressed(c.id) ? remove(c) : express(c); }}
              >
                {expressed(c.id) ? <><Icon name="check" size={14} /> Interested</> : "Show Interest"}
              </button>
            </div>
          </div>
        ))}
        {!loading && paginated.length === 0 && (
          <div className="empty-state" style={{ gridColumn: "1/-1" }}>
            <div className="empty-state-icon"><Icon name="briefcase" size={48} /></div>
            <p>No contracts match your filters.</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 24 }}>
          <button className="btn btn-sm btn-ghost" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
          <span style={{ fontSize: "0.82rem", color: "var(--muted)", fontFamily: "var(--mono)" }}>Page {page} of {totalPages}</span>
          <button className="btn btn-sm btn-ghost" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}

      {selected && (
        <div className="modal-overlay" onClick={() => setSelected(null)}>
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="contract-modal-title" onClick={e => e.stopPropagation()}>
            <button className="modal-close" aria-label="Close contract details" onClick={() => setSelected(null)}><Icon name="close" size={20} /></button>
            <div className="flex gap-8 mb-8">
              {selected.specialty && <span className="tag tag-specialty">{selected.specialty}</span>}
              {selected.urgent && <span className="tag tag-urgent">Urgent</span>}
            </div>
            <h2 id="contract-modal-title" className="modal-title">{selected.title}</h2>
            <div className="contract-meta" style={{ marginTop: 10 }}>
              {selected.location  && <span className="meta-chip"><Icon name="map" size={12} />{selected.location}</span>}
              {selected.duration  && <span className="meta-chip"><Icon name="clock" size={12} />{selected.duration}</span>}
              {selected.shift     && <span className="meta-chip"><Icon name="star" size={12} />{selected.shift}</span>}
              {selected.startDate && <span className="meta-chip"><Icon name="bell" size={12} />Starts {selected.startDate}</span>}
            </div>
            <div className="modal-section">
              <h4>Pay Rate</h4>
              <p className="pay-rate" style={{ fontSize: "1.4rem" }}>{selected.payRate}</p>
            </div>
            <div className="modal-section">
              <h4>Description</h4>
              <p style={{ fontSize: "0.88rem", color: "var(--muted)", lineHeight: 1.7 }}>{selected.description}</p>
            </div>
            {Array.isArray(selected.requirements) && selected.requirements.length > 0 && (
              <div className="modal-section">
                <h4>Requirements</h4>
                <ul className="req-list">
                  {selected.requirements.map((r, i) => (
                    <li key={i}><span style={{ color: "var(--teal)" }}><Icon name="check" size={14} /></span>{r}</li>
                  ))}
                </ul>
              </div>
            )}
            <div style={{ marginTop: 24 }}>
              <button
                className={`btn btn-full btn-interest ${expressed(selected.id) ? "expressed" : ""}`}
                onClick={() => { expressed(selected.id) ? remove(selected) : express(selected); setSelected(null); }}
              >
                {expressed(selected.id)
                  ? <><Icon name="check" size={16} /> Interest Withdrawn</>
                  : <><Icon name="bell" size={16} /> Express Interest — Your recruiter will contact you</>}
              </button>
            </div>
            <p className="text-sm text-muted" style={{ textAlign: "center", marginTop: 10 }}>
              Posted {fmtDate(selected.postedDate)}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// NURSE: PROFILE
// ============================================================
const EMPTY_PROFILE = {
  firstName: "", lastName: "", email: "", phone: "", address: "",
  city: "", province: "", postalCode: "", licenseNumber: "", licenseProvince: "",
  specialty: "", yearsExp: "", businessName: "", gstHstNumber: "",
  bankName: "", bankAccount: "", bankTransit: "", bankInstitution: "",
  sinMasked: "", notes: "",
};

function NurseProfile({ user, setUser }) {
  const [profile, setProfile] = useState(() => ({ ...EMPTY_PROFILE, ...user }));
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwError, setPwError] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [pwSaved, setPwSaved] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Nurses.me()
      .then(res => { if (!cancelled) setProfile(p => ({ ...p, ...res.user })); })
      .catch(e => { if (!cancelled) setError(e.message || "Failed to load profile"); });
    return () => { cancelled = true; };
  }, []);

  const set = (k, v) => setProfile(p => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true); setError("");
    try {
      const payload = {
        ...profile,
        yearsExp: profile.yearsExp === "" || profile.yearsExp == null
          ? null
          : Number(profile.yearsExp),
      };
      const res = await Nurses.updateMe(payload);
      setProfile(p => ({ ...p, ...res.user }));
      setUser(u => ({ ...(u || {}), ...res.user }));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e.message || "Failed to save profile");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">My Profile</h1>
        <p className="page-sub">Your information is encrypted and only accessible to authorized staff</p>
      </div>

      {saved && <div className="alert alert-success"><Icon name="check" size={14} /> Profile saved securely.</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="card mb-16">
        <h3 style={{ marginBottom: 20, fontSize: "0.9rem", letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--teal)" }}>Personal Information</h3>
        <div className="form-row">
          <div className="form-group"><label>First Name</label><input value={profile.firstName || ""} onChange={e => set("firstName", e.target.value)} /></div>
          <div className="form-group"><label>Last Name</label><input value={profile.lastName || ""} onChange={e => set("lastName", e.target.value)} /></div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Email <span style={{ fontWeight: 400, color: "var(--muted)", textTransform: "none", letterSpacing: 0 }}>(contact admin to change)</span></label>
            <input type="email" value={profile.email || ""} readOnly style={{ background: "var(--navy-light)", cursor: "not-allowed", color: "var(--muted)" }} />
          </div>
          <div className="form-group"><label>Phone</label><input value={profile.phone || ""} onChange={e => set("phone", e.target.value)} /></div>
        </div>
        <div className="form-group"><label>Street Address</label><input value={profile.address || ""} onChange={e => set("address", e.target.value)} /></div>
        <div className="form-row">
          <div className="form-group"><label>City</label><input value={profile.city || ""} onChange={e => set("city", e.target.value)} /></div>
          <div className="form-group"><label>Province</label>
            <select value={profile.province || ""} onChange={e => set("province", e.target.value)}>
              <option value="">Select...</option>
              {["ON","BC","AB","QC","MB","SK","NS","NB","NL","PE","NT","YT","NU"].map(p => <option key={p}>{p}</option>)}
            </select>
          </div>
          <div className="form-group"><label>Postal Code</label><input value={profile.postalCode || ""} onChange={e => set("postalCode", e.target.value)} placeholder="A1A 1A1" /></div>
        </div>
      </div>

      <div className="alert alert-info" style={{ marginBottom: 16 }}>
        <Icon name="file" size={14} /> Licence details and specialty are managed in your <strong>Documents</strong> tab.
      </div>

      <div className="card mb-24">
        <button className="btn btn-primary" style={{ minWidth: 200 }} onClick={save} disabled={busy}>
          <Icon name="shield" size={16} /> {busy ? "Saving..." : "Save Securely"}
        </button>
      </div>

      {/* Change Password */}
      {showChangePw && (
        <div className="card mb-16">
          <h3 style={{ marginBottom: 16, fontSize: "0.9rem", letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--teal)" }}>Change Password</h3>
          {pwSaved && <div className="alert alert-success"><Icon name="check" size={14} /> Password updated.</div>}
          {pwError && <div className="alert alert-error">{pwError}</div>}
          <div className="form-group"><label>Current Password</label><input type="password" value={pwForm.current} onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))} /></div>
          <div className="form-row">
            <div className="form-group"><label>New Password</label><input type="password" value={pwForm.next} onChange={e => setPwForm(f => ({ ...f, next: e.target.value }))} /></div>
            <div className="form-group"><label>Confirm New Password</label><input type="password" value={pwForm.confirm} onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))} /></div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="btn btn-primary btn-sm" disabled={pwSaving} onClick={async () => {
              setPwError("");
              if (!pwForm.current || !pwForm.next) { setPwError("All fields required."); return; }
              if (pwForm.next !== pwForm.confirm) { setPwError("Passwords do not match."); return; }
              if (pwForm.next.length < 8) { setPwError("Password must be at least 8 characters."); return; }
              setPwSaving(true);
              try {
                await Auth.changePassword({ currentPassword: pwForm.current, newPassword: pwForm.next });
                setPwSaved(true); setPwForm({ current: "", next: "", confirm: "" });
                setTimeout(() => { setPwSaved(false); setShowChangePw(false); }, 2000);
              } catch (e) { setPwError(e.message || "Failed to update password"); }
              finally { setPwSaving(false); }
            }}>{pwSaving ? "Saving…" : "Update Password"}</button>
            <button className="btn btn-ghost btn-sm" onClick={() => { setShowChangePw(false); setPwError(""); }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Delete account confirmation */}
      {showDeleteConfirm && (
        <div className="card mb-16" style={{ border: "1.5px solid rgba(220,38,38,0.3)" }}>
          <h3 style={{ marginBottom: 8, fontSize: "0.9rem", color: "var(--red)" }}>Confirm Account Deletion</h3>
          <p className="text-sm text-muted mb-16">This is permanent. All your data will be removed. Contact your recruiter if you have active contracts.</p>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="btn btn-danger btn-sm" onClick={async () => {
              try {
                await Auth.deleteAccount();
                clearTokens(); window.location.reload();
              } catch (e) { setError(e.message || "Failed to delete account"); setShowDeleteConfirm(false); }
            }}>Delete My Account</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Bottom action row */}
      <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
        <button className="btn btn-outline btn-sm" onClick={() => { setShowChangePw(v => !v); setShowDeleteConfirm(false); }}>
          <Icon name="lock" size={14} /> Change Password
        </button>
        <button className="btn btn-danger btn-sm" onClick={() => { setShowDeleteConfirm(v => !v); setShowChangePw(false); }}>
          <Icon name="trash" size={14} /> Delete Account
        </button>
      </div>
    </div>
  );
}

// ============================================================
// NURSE: DOCUMENTS (10-category system)
// ============================================================
function CategoryUploadBox({ category, docs, onUpload, onDelete, onDownload, uploading }) {
  const [drag, setDrag] = useState(false);
  const fileRef = useRef();
  const catDocs = docs.filter(d => d.categoryKey === category.key);
  const fmtSize = (b) => b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`;

  const status = catDocs.length > 0 ? "complete" : category.required ? "missing" : "optional";
  const statusColor = { complete: "var(--teal)", missing: "var(--red)", optional: "var(--muted)" };
  const statusLabel = { complete: "Complete", missing: "Required", optional: "Optional" };

  return (
    <div className="card" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontWeight: 700, fontSize: "0.9rem" }}>{category.label}</span>
            <span style={{ fontSize: "0.68rem", fontWeight: 700, padding: "2px 8px", borderRadius: 99, background: status === "complete" ? "rgba(0,51,102,0.08)" : status === "missing" ? "rgba(232,72,85,0.1)" : "rgba(107,114,128,0.08)", color: statusColor[status], border: `1px solid ${statusColor[status]}30` }}>
              {statusLabel[status]}
            </span>
          </div>
          {category.description && (
            <p style={{ fontSize: "0.78rem", color: "var(--muted)", marginTop: 4, lineHeight: 1.5 }}>{category.description}</p>
          )}
        </div>
        <div style={{ width: 32, height: 32, borderRadius: "50%", background: status === "complete" ? "var(--teal)" : "var(--border)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          {status === "complete" ? <Icon name="check" size={14} color="var(--navy)" /> : <Icon name="upload" size={14} color="var(--muted)" />}
        </div>
      </div>

      {catDocs.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {catDocs.map(doc => (
            <div key={doc.id} style={{ display: "flex", alignItems: "center", gap: 10, background: "var(--navy-mid)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", padding: "10px 12px", cursor: "pointer" }}
              onClick={() => onDownload(doc.id)} title="Click to preview">
              <div style={{ color: "var(--teal)", flexShrink: 0, lineHeight: 0 }}><Icon name="file" size={16} /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: "0.82rem", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.name}</div>
                <div style={{ fontSize: "0.72rem", color: "var(--muted)", fontFamily: "var(--mono)", marginTop: 2 }}>{fmtSize(doc.size)} · {fmtDate(doc.uploadedAt)}</div>
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                <button aria-label="Preview document" className="btn btn-sm btn-outline" style={{ padding: "4px 10px", fontSize: "0.72rem" }} onClick={() => onDownload(doc.id)}><Icon name="eye" size={12} /></button>
                <button aria-label="Delete document" className="btn btn-sm btn-danger" style={{ padding: "4px 10px", fontSize: "0.72rem" }} onClick={() => onDelete(doc.id)}><Icon name="trash" size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div
        className={`upload-zone ${drag ? "drag-over" : ""}`}
        style={{ padding: catDocs.length > 0 ? "10px 12px" : "16px 12px" }}
        onClick={() => fileRef.current.click()}
        onDragOver={e => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={e => { e.preventDefault(); setDrag(false); onUpload(Array.from(e.dataTransfer.files), category.key); }}
      >
        <div className="upload-zone-icon" style={{ marginBottom: 4 }}><Icon name="upload" size={18} /></div>
        <p style={{ fontSize: "0.75rem" }}><strong>{uploading === category.key ? "Uploading…" : catDocs.length > 0 ? "Add another file" : "Click or drag to upload"}</strong></p>
        <input ref={fileRef} type="file" multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt" style={{ display: "none" }}
          onChange={e => onUpload(Array.from(e.target.files), category.key)} />
      </div>
    </div>
  );
}

function NurseDocuments() {
  const [categories, setCats] = useState([]);
  const [docs, setDocs] = useState([]);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(null);
  const [profDetails, setProfDetails] = useState({ licenseNumber: "", licenseProvince: "", yearsExp: "" });
  const [licences, setLicences] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [addingLicence, setAddingLicence] = useState(false);
  const [newLicence, setNewLicence] = useState({ number: "", province: "", expiry: "" });
  const [addingSpecialty, setAddingSpecialty] = useState(false);
  const [newSpecialty, setNewSpecialty] = useState("");
  const [profSaved, setProfSaved] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [catRes, docRes, meRes] = await Promise.all([Categories.list(), Documents.list(), Nurses.me()]);
      setCats(catRes.categories || []);
      setDocs(docRes.documents || []);
      const u = meRes.user || {};
      setProfDetails({ licenseNumber: u.licenseNumber || "", licenseProvince: u.licenseProvince || "", yearsExp: u.yearsExp ?? "" });
      setLicences(u.additionalLicences || []);
      setSpecialties(u.additionalSpecialties || (u.specialty ? [u.specialty] : []));
    } catch (e) {
      setError(e.message || "Failed to load documents");
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleUpload = async (files, categoryKey) => {
    if (!files.length) return;
    setUploading(categoryKey); setError("");
    try {
      for (const f of files) {
        if (f.size > 10 * 1024 * 1024) { setError(`${f.name} exceeds 10 MB`); continue; }
        await Documents.upload(f, categoryKey);
      }
      await refresh();
    } catch (e) {
      setError(e.message || "Upload failed");
    } finally {
      setUploading(null);
    }
  };

  const handleDelete = async (id) => {
    const prev = docs;
    setDocs(d => d.filter(x => x.id !== id));
    try { await Documents.remove(id); }
    catch (e) { setDocs(prev); setError(e.message || "Failed to delete"); }
  };

  const handleDownload = async (id) => {
    try {
      const { url } = await Documents.downloadUrl(id);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) { setError(e.message || "Failed to fetch download link"); }
  };

  const total = categories.length;
  const complete = categories.filter(c => docs.some(d => d.categoryKey === c.key)).length;

  return (
    <div>
      <div className="page-header">
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <h1 className="page-title">My Documents</h1>
            <p className="page-sub">Upload your credentials and compliance documents for each category</p>
          </div>
          <div className="stat-card" style={{ minWidth: 140, padding: 16 }}>
            <div className="stat-label">Progress</div>
            <div className="stat-value" style={{ fontSize: "1.5rem" }}>{complete}<span style={{ fontSize: "1rem", color: "var(--muted)" }}>/{total}</span></div>
            <div style={{ height: 4, background: "var(--border)", borderRadius: 2, marginTop: 8 }}>
              <div style={{ height: "100%", width: `${total ? (complete / total) * 100 : 0}%`, background: "var(--teal)", borderRadius: 2, transition: "width 0.4s" }} />
            </div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {profSaved && <div className="alert alert-success"><Icon name="check" size={14} /> Professional details saved.</div>}

      {/* Professional Details */}
      <div className="card mb-16">
        <h3 style={{ marginBottom: 16, fontSize: "0.9rem", letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--teal)" }}>Professional Details</h3>
        <div className="form-row">
          <div className="form-group"><label>License Number</label><input value={profDetails.licenseNumber} onChange={e => setProfDetails(p => ({ ...p, licenseNumber: e.target.value }))} /></div>
          <div className="form-group"><label>License Province</label>
            <select value={profDetails.licenseProvince} onChange={e => setProfDetails(p => ({ ...p, licenseProvince: e.target.value }))}>
              <option value="">Select...</option>
              {["ON","BC","AB","QC","MB","SK","NS","NB","NL","PE"].map(pr => <option key={pr}>{pr}</option>)}
            </select>
          </div>
          <div className="form-group"><label>Years of Experience</label><input type="number" min="0" value={profDetails.yearsExp} onChange={e => setProfDetails(p => ({ ...p, yearsExp: e.target.value }))} /></div>
        </div>
        <button className="btn btn-sm btn-primary" onClick={async () => {
          try {
            await Nurses.updateMe({ ...profDetails, yearsExp: profDetails.yearsExp === "" ? null : Number(profDetails.yearsExp) });
            setProfSaved(true); setTimeout(() => setProfSaved(false), 2000);
          } catch (e) { setError(e.message || "Failed to save"); }
        }}>Save Details</button>
      </div>

      {/* Licences */}
      <div className="card mb-16">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <h3 style={{ fontSize: "0.9rem", letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--teal)" }}>Licences</h3>
          <button className="btn btn-sm btn-outline" onClick={() => setAddingLicence(v => !v)}><Icon name="plus" size={13} /> Add Licence</button>
        </div>
        {addingLicence && (
          <div style={{ background: "var(--navy-mid)", borderRadius: 10, padding: 14, marginBottom: 14 }}>
            <div className="form-row">
              <div className="form-group"><label>Licence #</label><input value={newLicence.number} onChange={e => setNewLicence(l => ({ ...l, number: e.target.value }))} /></div>
              <div className="form-group"><label>Province</label>
                <select value={newLicence.province} onChange={e => setNewLicence(l => ({ ...l, province: e.target.value }))}>
                  <option value="">Select...</option>
                  {["ON","BC","AB","QC","MB","SK","NS","NB","NL","PE"].map(pr => <option key={pr}>{pr}</option>)}
                </select>
              </div>
              <div className="form-group"><label>Expiry</label><input type="date" value={newLicence.expiry} onChange={e => setNewLicence(l => ({ ...l, expiry: e.target.value }))} /></div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-sm btn-primary" onClick={async () => {
                if (!newLicence.number || !newLicence.province) { setError("Licence number and province required."); return; }
                const updated = [...licences, newLicence];
                try { await Nurses.updateMe({ additionalLicences: updated }); setLicences(updated); setNewLicence({ number: "", province: "", expiry: "" }); setAddingLicence(false); }
                catch (e) { setError(e.message || "Failed to save"); }
              }}>Add</button>
              <button className="btn btn-sm btn-ghost" onClick={() => setAddingLicence(false)}>Cancel</button>
            </div>
          </div>
        )}
        {licences.length === 0 ? <p className="text-muted text-sm">No additional licences added.</p> : licences.map((l, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid var(--border)" }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: "0.88rem" }}>{l.number} · {l.province}</div>
              {l.expiry && <div className="text-sm text-muted">Expires {l.expiry}</div>}
            </div>
            <button aria-label="Remove licence" className="btn btn-sm btn-danger" onClick={async () => {
              const updated = licences.filter((_, j) => j !== i);
              try { await Nurses.updateMe({ additionalLicences: updated }); setLicences(updated); }
              catch (e) { setError(e.message || "Failed to remove"); }
            }}><Icon name="trash" size={13} /></button>
          </div>
        ))}
      </div>

      {/* Specialties */}
      <div className="card mb-16">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <h3 style={{ fontSize: "0.9rem", letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--teal)" }}>Specialties</h3>
          <button className="btn btn-sm btn-outline" onClick={() => setAddingSpecialty(v => !v)}><Icon name="plus" size={13} /> Add Specialty</button>
        </div>
        {addingSpecialty && (
          <div style={{ display: "flex", gap: 8, marginBottom: 14, alignItems: "flex-end" }}>
            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
              <select value={newSpecialty} onChange={e => setNewSpecialty(e.target.value)}>
                <option value="">Select specialty...</option>
                {["ICU","Emergency","OR","Pediatrics","Oncology","Med-Surg","Cardiac","Neurology","NICU","Obstetrics","Psychiatry","Home Care","Other"].filter(s => !specialties.includes(s)).map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <button className="btn btn-sm btn-primary" onClick={async () => {
              if (!newSpecialty) return;
              const updated = [...specialties, newSpecialty];
              try { await Nurses.updateMe({ specialties: updated }); setSpecialties(updated); setNewSpecialty(""); setAddingSpecialty(false); }
              catch (e) { setError(e.message || "Failed to save"); }
            }}>Add</button>
            <button className="btn btn-sm btn-ghost" onClick={() => setAddingSpecialty(false)}>Cancel</button>
          </div>
        )}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {specialties.length === 0 ? <p className="text-muted text-sm">No specialties added.</p> : specialties.map((s, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, background: "var(--navy-mid)", border: "1px solid var(--border)", borderRadius: 99, padding: "4px 12px 4px 14px" }}>
              <span style={{ fontSize: "0.82rem", fontWeight: 600 }}>{s}</span>
              <button aria-label={`Remove ${s} specialty`} onClick={async () => {
                const updated = specialties.filter((_, j) => j !== i);
                try { await Nurses.updateMe({ specialties: updated }); setSpecialties(updated); }
                catch (e) { setError(e.message || "Failed to remove"); }
              }} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)", lineHeight: 0, padding: 2 }}><Icon name="close" size={12} /></button>
            </div>
          ))}
        </div>
      </div>

      {/* Document uploads */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
        {categories.map(cat => (
          <CategoryUploadBox
            key={cat.key}
            category={cat}
            docs={docs}
            onUpload={handleUpload}
            onDelete={handleDelete}
            onDownload={handleDownload}
            uploading={uploading}
          />
        ))}
      </div>

      <div className="alert alert-info" style={{ marginTop: 20 }}>
        <Icon name="shield" size={14} /> All documents are stored AES-256 encrypted in S3. Only authorized admin staff can access them.
      </div>
    </div>
  );
}

// ============================================================
// NURSE DASHBOARD
// ============================================================
function NurseDashboard({ user, setUser, setPage }) {
  const [counts, setCounts] = useState({ interests: 0, contracts: 0 });
  const [toggling, setToggling] = useState(false);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([Interests.mine(), Contracts.list()])
      .then(([i, c]) => {
        if (cancelled) return;
        setCounts({
          interests: (i.contractIds || []).length,
          contracts: (c.contracts || []).length,
        });
      })
      .catch(() => {});
    // Load admin-triggered notifications (schedules, payroll, doc alerts)
    Notifications.mySettings()
      .then(r => { if (!cancelled) setNotifications(r.notices || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const toggleContractInterest = async () => {
    setToggling(true);
    try {
      const res = await Nurses.toggleContractInterest(!user.contractInterestOpen);
      setUser(u => ({ ...u, contractInterestOpen: res.user.contractInterestOpen }));
    } catch {}
    finally { setToggling(false); }
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Welcome back, {user.firstName || user.name}</h1>
      </div>
      <div className="stats-row">
        <div className="stat-card" style={{ cursor: "pointer" }} onClick={() => setPage("contracts")}>
          <div className="stat-icon"><Icon name="star" size={20} /></div>
          <div className="stat-label">My Interests</div>
          <div className="stat-value">{counts.interests}</div>
        </div>
        <div className="stat-card" style={{ cursor: "pointer" }} onClick={() => setPage("contracts")}>
          <div className="stat-icon"><Icon name="briefcase" size={20} /></div>
          <div className="stat-label">Open Contracts</div>
          <div className="stat-value">{counts.contracts}</div>
        </div>
      </div>

      {/* Global contract interest toggle */}
      <div className="card mb-16" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, padding: "20px 24px" }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: "0.95rem" }}>Open to Contract Opportunities</div>
          <div className="text-sm text-muted">Let your recruiter know you&apos;re available for contract work</div>
        </div>
        <button
          onClick={toggleContractInterest}
          disabled={toggling}
          style={{ width: 52, height: 28, borderRadius: 99, background: user.contractInterestOpen ? "var(--teal)" : "var(--border)", border: "none", cursor: "pointer", position: "relative", transition: "background 0.2s", flexShrink: 0 }}
        >
          <span style={{ position: "absolute", top: 3, left: user.contractInterestOpen ? 26 : 3, width: 22, height: 22, borderRadius: "50%", background: "#fff", transition: "left 0.2s", display: "block" }} />
        </button>
      </div>

      {/* Interest action */}
      <div className="card mb-16" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, padding: "20px 24px" }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: "0.95rem" }}>Browse Contracts</div>
          <div className="text-sm text-muted">View open contracts and express or remove interest</div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setPage("contracts")}>
          <Icon name="briefcase" size={14} /> View Contracts
        </button>
      </div>

      {/* Notification board */}
      <div className="card">
        <h3 style={{ marginBottom: 12, fontSize: "0.9rem", color: "var(--teal)", letterSpacing: "0.06em", textTransform: "uppercase" }}>Notifications</h3>
        {notifications.length === 0 ? (
          <p className="text-muted text-sm" style={{ padding: "12px 0" }}>No new notifications from your recruiter.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {notifications.map((n, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 0", borderBottom: "1px solid var(--border)" }}>
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--bc-teal-soft)", border: "1px solid rgba(14,116,144,0.25)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon name="bell" size={13} color="var(--bc-teal)" />
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: "0.88rem" }}>{n.title || n.label}</div>
                  {n.desc && <div className="text-sm text-muted">{n.desc}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// NURSE: SCHEDULES
// ============================================================
function NurseSchedules() {
  const [schedules, setSchedules] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Schedules.list()
      .then(r => { if (!cancelled) setSchedules(r.schedules || []); })
      .catch(e => { if (!cancelled) setError(e.message || "Failed to load schedules"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const open = async (id) => {
    try {
      const { url } = await Schedules.downloadUrl(id);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) { setError(e.message || "Failed to open schedule"); }
  };

  const current = schedules[0] || null;
  const history = schedules.slice(1);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">My Schedules</h1>
        <p className="page-sub">Your current and past schedules posted by NLC Staffing</p>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      {loading && <p className="text-muted">Loading…</p>}
      {!loading && schedules.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon"><Icon name="clock" size={48} /></div>
          <p>No schedules have been posted yet.</p>
        </div>
      )}

      {!loading && current && (
        <div style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: "0.8rem", fontWeight: 700, color: "var(--teal)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12 }}>
            Current Schedule
          </h2>
          <div className="card" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, padding: "20px 24px", border: "2px solid var(--teal)", background: "var(--soft)" }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: "1.05rem" }}>{current.title}</div>
              <div className="text-muted text-sm" style={{ marginTop: 4 }}>{current.periodLabel} · Posted {fmtDate(current.createdAt)}</div>
            </div>
            <button className="btn btn-sm btn-primary" onClick={() => open(current.id)}>
              <Icon name="eye" size={14} /> Open
            </button>
          </div>
        </div>
      )}

      {!loading && history.length > 0 && (
        <div>
          <h2 style={{ fontSize: "0.8rem", fontWeight: 700, color: "var(--teal)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12 }}>
            Schedule History
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {history.map(s => (
              <div key={s.id} className="card" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, padding: "14px 20px" }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{s.title}</div>
                  <div className="text-muted text-sm">{s.periodLabel} · Posted {fmtDate(s.createdAt)}</div>
                </div>
                <button className="btn btn-sm btn-outline" onClick={() => open(s.id)}>
                  <Icon name="eye" size={14} /> Open
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// NURSE: NOTIFICATION SETTINGS
// ============================================================
function NurseNotificationSettings() {
  const [settings, setSettings] = useState({ schedule_upload: true, payroll_reminder: true });
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    Notifications.mySettings()
      .then(r => setSettings(r.settings || {}))
      .catch(e => setError(e.message || "Failed to load settings"));
  }, []);

  const toggle = async (key) => {
    const next = { ...settings, [key]: !settings[key] };
    setSettings(next);
    try {
      await Notifications.updateMySettings({ [key]: next[key] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { setError(e.message || "Failed to save"); }
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Notifications</h1>
        <p className="page-sub">Manage your email notification preferences</p>
      </div>
      {saved && <div className="alert alert-success"><Icon name="check" size={14} /> Saved.</div>}
      {error && <div className="alert alert-error">{error}</div>}
      <div className="card">
        {[
          { key: "schedule_upload", label: "Schedule posted", desc: "Email me when a new schedule is uploaded" },
          { key: "payroll_reminder", label: "Weekly payroll reminder", desc: "Remind me each week to submit timesheets" },
          { key: "new_contract_alert", label: "New contract alert", desc: "Email me when a new contract is posted" },
        ].map(item => (
          <div key={item.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 0", borderBottom: "1px solid var(--border)" }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>{item.label}</div>
              <div className="text-sm text-muted">{item.desc}</div>
            </div>
            <button
              onClick={() => toggle(item.key)}
              style={{ width: 44, height: 24, borderRadius: 99, background: settings[item.key] ? "var(--teal)" : "var(--border)", border: "none", cursor: "pointer", position: "relative", transition: "background 0.2s", flexShrink: 0 }}
            >
              <span style={{ position: "absolute", top: 2, left: settings[item.key] ? 22 : 2, width: 20, height: 20, borderRadius: "50%", background: "#fff", transition: "left 0.2s", display: "block" }} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// ADMIN: DASHBOARD
// ============================================================
function AdminDashboard({ setPage }) {
  const [stats, setStats] = useState({ nurses: 0, contracts: 0, interests: 0, docs: 0, available: 0, pendingAuth: 0, contractsAvailable: 0, onboarded: 0 });
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const [n, c, i] = await Promise.all([Nurses.list(), Contracts.list(), Interests.byContract()]);
      const nurseList = n.nurses || [];
      const contractList = c.contracts || [];
      const contractsWithInterest = new Set((i.contracts || []).filter(x => x.nurses?.length > 0).map(x => x.contractId));
      const totalInterests = (i.contracts || []).reduce((acc, x) => acc + (x.nurses?.length || 0), 0);
      const totalDocs = nurseList.reduce((acc, x) => acc + (x.documentCount || 0), 0);
      const available = nurseList.filter(x => x.contractInterestOpen).length;
      const pendingAuth = nurseList.filter(x => !x.contractAuthorized).length;
      const onboarded = nurseList.filter(x => x.contractAuthorized && x.profileComplete).length;
      const contractsAvailable = contractList.filter(x => !contractsWithInterest.has(x.id)).length;
      const activeContracts = contractList.filter(x => {
        if (!x.endDate) return true;
        return new Date(x.endDate) >= new Date();
      }).length;
      setStats({
        nurses: n.total ?? nurseList.length,
        contracts: activeContracts,
        interests: totalInterests,
        docs: totalDocs,
        available,
        pendingAuth,
        contractsAvailable,
        onboarded,
      });
    } catch (e) { setError(e.message || "Failed to load dashboard"); }
  }, []);

  useEffect(() => { let cancelled = false; refresh(); return () => { cancelled = true; }; }, [refresh]);

  const today = new Date().toLocaleDateString("en-CA", { weekday: "long", year: "numeric", month: "long", day: "numeric" });

  return (
    <div>
      <div className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Admin Dashboard</h1>
            <p className="page-sub">{today}</p>
          </div>
          <span className="badge-pill">{stats.nurses} registered · {stats.onboarded} on-boarded</span>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Primary stats */}
      <div className="stats-row">
        <div className="stat-card" style={{ cursor: "pointer" }} onClick={() => setPage("research")} title="View on-boarded nurses">
          <div className="stat-icon"><Icon name="user" size={20} /></div>
          <div className="stat-label">On-Boarded Nurses</div>
          <div className="stat-value">{stats.onboarded}</div>
        </div>
        <div className="stat-card" style={{ borderTop: "3px solid #10B981", cursor: "pointer" }} onClick={() => setPage("availability")} title="View availability">
          <div className="stat-icon" style={{ color: "#10B981" }}><Icon name="pulse" size={20} /></div>
          <div className="stat-label">Available Now</div>
          <div className="stat-value" style={{ color: "#10B981" }}>{stats.available}</div>
        </div>
        <div className="stat-card" style={{ borderTop: "3px solid var(--amber)", cursor: "pointer" }} onClick={() => setPage("research")} title="View pending nurses">
          <div className="stat-icon" style={{ color: "var(--amber)" }}><Icon name="clock" size={20} /></div>
          <div className="stat-label">Pending Auth</div>
          <div className="stat-value" style={{ color: "var(--amber)" }}>{stats.pendingAuth}</div>
        </div>
        <div className="stat-card" style={{ cursor: "pointer" }} onClick={() => setPage("posted-contracts")} title="View active contracts">
          <div className="stat-icon"><Icon name="briefcase" size={20} /></div>
          <div className="stat-label">Active Contracts</div>
          <div className="stat-value">{stats.contracts}</div>
        </div>
        <div className="stat-card" style={{ cursor: "pointer" }} onClick={() => setPage("posted-contracts")} title="Contracts with no applicants">
          <div className="stat-icon"><Icon name="plus" size={20} /></div>
          <div className="stat-label">Contracts Available</div>
          <div className="stat-value">{stats.contractsAvailable}</div>
        </div>
        <div className="stat-card" style={{ cursor: "pointer" }} onClick={() => setPage("interests")} title="View all interests">
          <div className="stat-icon"><Icon name="bell" size={20} /></div>
          <div className="stat-label">Interests</div>
          <div className="stat-value">{stats.interests}</div>
        </div>
      </div>

    </div>
  );
}

// ============================================================
// ADMIN: NURSE PROFILE PAGE (full-page version of the profile)
// ============================================================
function NurseProfilePage({ nurse: initialNurse, onBack }) {
  const [nurse, setNurse] = useState(initialNurse);
  const [docs, setDocs] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [interestedContracts, setInterestedContracts] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [designation, setDesignation] = useState(initialNurse.designation || "Staff");
  const [editNotes, setEditNotes] = useState(initialNurse.notes || "");
  const [schedForm, setSchedForm] = useState({ title: "", periodLabel: "" });
  const [uploading, setUploading] = useState(false);
  const [schedSuccess, setSchedSuccess] = useState("");
  const schedFileRef = useRef();

  // Email tab state
  const [emailForm, setEmailForm] = useState({ subject: "", body: "" });
  const [emailStep, setEmailStep] = useState(0); // 0=compose, 1=confirm
  const [emailSending, setEmailSending] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState("");
  const [selectedContract, setSelectedContract] = useState(null);

  const loadSchedules = useCallback(async () => {
    try {
      const r = await Schedules.listForNurse(nurse.id);
      setSchedules(r.schedules || []);
    } catch {}
  }, [nurse.id]);

  useEffect(() => {
    Documents.list(nurse.id)
      .then(r => setDocs(r.documents || []))
      .catch(() => {});
    Nurses.get(nurse.id)
      .then(r => {
        setNurse(n => ({ ...n, ...r.user }));
        setDesignation(r.user.designation || "Staff");
        setEditNotes(r.user.notes || "");
      })
      .catch(() => {});
    Interests.byNurse(nurse.id)
      .then(r => setInterestedContracts(r.contracts || []))
      .catch(() => {});
    loadSchedules();
  }, [nurse.id, loadSchedules]);

  const toggleAuth = async () => {
    const next = !nurse.contractAuthorized;
    setSaving(true);
    try {
      const res = await Nurses.authorizeContract(nurse.id, next);
      setNurse(n => ({ ...n, ...res.user }));
    } catch (e) { setError(e.message || "Failed to update authorization"); }
    finally { setSaving(false); }
  };

  const toggleAvailability = async () => {
    const next = !nurse.contractInterestOpen;
    setSaving(true);
    try {
      await Nurses.setAvailability(nurse.id, next);
      setNurse(n => ({ ...n, contractInterestOpen: next }));
    } catch (e) { setError(e.message || "Failed to update availability"); }
    finally { setSaving(false); }
  };

  const saveDesignation = async (d) => {
    setDesignation(d);
    try {
      await Nurses.update(nurse.id, { designation: d });
    } catch (e) { setError(e.message || "Failed to save designation"); }
  };

  const saveNotes = async () => {
    setSaving(true);
    try {
      await Nurses.update(nurse.id, { notes: editNotes });
      setNurse(n => ({ ...n, notes: editNotes }));
    } catch (e) { setError(e.message || "Failed to save notes"); }
    finally { setSaving(false); }
  };

  const handleScheduleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!schedForm.title || !schedForm.periodLabel) {
      setError("Schedule title and period label are required."); return;
    }
    setUploading(true); setError("");
    try {
      await Schedules.upload(file, schedForm.title, schedForm.periodLabel, nurse.id);
      setSchedForm({ title: "", periodLabel: "" });
      setSchedSuccess("Schedule uploaded and nurse notified.");
      setTimeout(() => setSchedSuccess(""), 4000);
      await loadSchedules();
    } catch (e) { setError(e.message || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const exp = calcExpScore(nurse);
  const name = `${nurse.firstName || ""} ${nurse.lastName || ""}`.trim() || "—";
  const tierColors = { Senior: "var(--bc-teal)", Intermediate: "var(--teal)", Junior: "var(--amber)", Entry: "var(--muted)" };

  return (
    <div>
      {/* Back breadcrumb */}
      <button className="btn btn-ghost btn-sm" style={{ marginBottom: 20 }} onClick={onBack}>
        ← Back to Nurse Research
      </button>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 24 }}>
        <div style={{ width: 60, height: 60, borderRadius: "50%", background: "var(--teal)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, color: "#fff", fontSize: "1.2rem", flexShrink: 0 }}>
          {name[0]?.toUpperCase() || "?"}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <h1 className="page-title" style={{ marginBottom: 0 }}>{name}</h1>
            <span className={`designation-badge ${designation === "INC" ? "designation-inc" : "designation-staff"}`}>
              {designation === "INC" ? "INC" : "Staff"}
            </span>
            <span style={{ fontSize: "0.68rem", fontWeight: 700, padding: "3px 9px", borderRadius: 99,
              background: nurse.contractAuthorized ? "rgba(0,51,102,0.07)" : "rgba(217,119,6,0.09)",
              color: nurse.contractAuthorized ? "var(--teal)" : "var(--amber)",
              border: `1px solid ${nurse.contractAuthorized ? "rgba(0,51,102,0.18)" : "rgba(217,119,6,0.22)"}` }}>
              {nurse.contractAuthorized ? "Contract Authorized" : "Pending Auth"}
            </span>
          </div>
          <p className="text-muted text-sm" style={{ marginTop: 4 }}>{nurse.email}{nurse.phone && ` · ${nurse.phone}`}</p>
        </div>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      {/* Designation toggle */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        <button className={`btn btn-sm ${designation === "Staff" ? "btn-primary" : "btn-ghost"}`} onClick={() => saveDesignation("Staff")}>Staff</button>
        <button
          className={`btn btn-sm ${designation === "INC" ? "btn-outline" : "btn-ghost"}`}
          style={designation === "INC" ? { background: "rgba(14,116,144,0.1)", color: "var(--bc-teal)", borderColor: "rgba(14,116,144,0.3)" } : {}}
          onClick={() => saveDesignation("INC")}
        >INC (Independent Contractor)</button>
      </div>

      {/* Profile tabs */}
      <div className="profile-tab-bar" role="tablist" aria-label="Profile sections">
        {["overview", "experience", "documents", "schedules", "comparative", "email", "notes"].map(t => (
          <button key={t} role="tab" aria-selected={activeTab === t} className={`profile-tab ${activeTab === t ? "active" : ""}`} onClick={() => setActiveTab(t)}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* OVERVIEW TAB */}
      {activeTab === "overview" && (
        <div>
          {[
            ["License #", nurse.licenseNumber], ["License Province", nurse.licenseProvince],
            ["Specialty", nurse.specialty], ["Province", nurse.province],
            ["GST/HST #", nurse.gstHstNumber], ["Business Name", nurse.businessName],
            ["Bank", nurse.bankName], ["Registered", fmtDate(nurse.registeredAt)],
          ].map(([k, v]) => v ? (
            <div key={k} style={{ display: "flex", borderBottom: "1px solid var(--border)", padding: "9px 0" }}>
              <span style={{ width: 180, color: "var(--muted)", fontSize: "0.8rem", flexShrink: 0 }}>{k}</span>
              <span style={{ fontSize: "0.875rem" }}>{v}</span>
            </div>
          ) : null)}

          {/* Contract authorization */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 20, padding: "16px 0", borderTop: "1px solid var(--border)" }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: "0.9rem" }}>Contract Authorization</div>
              <div className="text-sm text-muted">Allows nurse to view full contract details & apply</div>
            </div>
            <button
              onClick={toggleAuth}
              disabled={saving}
              style={{ padding: "9px 18px", borderRadius: 10, fontWeight: 700, fontSize: "0.82rem", cursor: "pointer", border: "none",
                background: nurse.contractAuthorized ? "rgba(220,38,38,0.1)" : "var(--teal)",
                color: nurse.contractAuthorized ? "var(--red)" : "#fff", transition: "all 0.2s", opacity: saving ? 0.6 : 1 }}
            >
              {nurse.contractAuthorized ? "Revoke Authorization" : "Approve for Contracts"}
            </button>
          </div>

          {/* Availability toggle */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 0", borderTop: "1px solid var(--border)" }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: "0.9rem" }}>Open to Contract Opportunities</div>
              <div className="text-sm text-muted">Nurse is actively available for new contracts</div>
            </div>
            <button
              onClick={toggleAvailability}
              disabled={saving}
              style={{ width: 44, height: 24, borderRadius: 99,
                background: nurse.contractInterestOpen ? "#10B981" : "var(--border)",
                border: "none", cursor: "pointer", position: "relative",
                transition: "background 0.2s", flexShrink: 0, opacity: saving ? 0.5 : 1 }}
            >
              <span style={{ position: "absolute", top: 2, left: nurse.contractInterestOpen ? 22 : 2,
                width: 20, height: 20, borderRadius: "50%", background: "#fff",
                transition: "left 0.2s", display: "block" }} />
            </button>
          </div>

          <button className="btn btn-outline btn-full" style={{ marginTop: 12 }} onClick={() => setActiveTab("email")}>
            <Icon name="bell" size={15} /> Send Email to {nurse.firstName}
          </button>
        </div>
      )}

      {/* EXPERIENCE TAB */}
      {activeTab === "experience" && (
        <div>
          <div className="card" style={{ marginBottom: 16, background: "var(--navy-mid)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ fontWeight: 700 }}>Experience Score</span>
              <span style={{ fontFamily: "var(--mono)", fontSize: "1.4rem", fontWeight: 800, color: tierColors[exp.tier] }}>
                {exp.pct}<span style={{ fontSize: "0.8rem", color: "var(--muted)" }}>/100</span>
              </span>
            </div>
            <div className="exp-bar"><div className="exp-bar-fill" style={{ width: `${exp.pct}%` }} /></div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10 }}>
              <span style={{ fontSize: "0.8rem", color: "var(--muted)" }}>{exp.yrs} yrs × {exp.weight}x specialty multiplier</span>
              <span style={{ fontSize: "0.75rem", fontWeight: 800, padding: "3px 10px", borderRadius: 99,
                background: `${tierColors[exp.tier]}15`, color: tierColors[exp.tier],
                border: `1px solid ${tierColors[exp.tier]}40` }}>{exp.tier}</span>
            </div>
          </div>
          <h4 style={{ fontSize: "0.72rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 12, fontWeight: 700 }}>Qualification Assessment</h4>
          {[
            { label: "Has license number on file", done: !!nurse.licenseNumber },
            { label: "Province of license recorded", done: !!nurse.licenseProvince },
            { label: "Specialty declared", done: !!nurse.specialty },
            { label: "Years of experience entered", done: !!nurse.yearsExp },
            { label: "Banking details on file", done: !!(nurse.bankName && nurse.bankAccount) },
            { label: "SIN encrypted on file", done: !!nurse.sinMasked },
            { label: "GST/HST number (if INC)", done: designation !== "INC" || !!nurse.gstHstNumber },
            { label: "Contract authorized by admin", done: !!nurse.contractAuthorized },
            { label: "Documents uploaded", done: docs.length > 0 },
          ].map((q, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderBottom: "1px solid var(--border)" }}>
              <div style={{ width: 22, height: 22, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                background: q.done ? "var(--teal)" : "var(--border)" }}>
                {q.done ? <Icon name="check" size={12} color="#fff" /> : <span style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--muted)", display: "block" }} />}
              </div>
              <span style={{ fontSize: "0.875rem", color: q.done ? "var(--white)" : "var(--muted)" }}>{q.label}</span>
            </div>
          ))}
          <div style={{ marginTop: 16, padding: "12px 16px", borderRadius: 10, background: "var(--navy-mid)", border: "1px solid var(--border)" }}>
            <div style={{ fontSize: "0.8rem", color: "var(--muted)", fontWeight: 600 }}>
              Completion: {Math.round(([!!nurse.licenseNumber, !!nurse.licenseProvince, !!nurse.specialty, !!nurse.yearsExp, !!(nurse.bankName && nurse.bankAccount), !!nurse.sinMasked, designation !== "INC" || !!nurse.gstHstNumber, !!nurse.contractAuthorized, docs.length > 0].filter(Boolean).length / 9) * 100)}%
            </div>
          </div>
        </div>
      )}

      {/* DOCUMENTS TAB */}
      {activeTab === "documents" && (
        <div>
          {docs.length === 0
            ? <div className="empty-state" style={{ padding: "40px 24px" }}><div className="empty-state-icon"><Icon name="file" size={40} /></div><p>No documents uploaded yet.</p></div>
            : docs.map(d => (
              <div key={d.id} className="file-item" style={{ marginBottom: 8 }}>
                <div className="file-icon"><Icon name="file" size={16} /></div>
                <div className="file-info">
                  <div className="file-name">{d.name}</div>
                  <div className="file-meta">{d.category || d.categoryKey} · {fmtDate(d.uploadedAt)}</div>
                </div>
                <button className="btn btn-sm btn-outline" onClick={async () => {
                  try { const { url } = await Documents.downloadUrl(d.id); window.open(url, "_blank", "noopener,noreferrer"); }
                  catch (e) { setError(e.message || "Failed to fetch download URL"); }
                }}><Icon name="eye" size={13} /> View</button>
              </div>
            ))
          }
        </div>
      )}

      {/* SCHEDULES TAB */}
      {activeTab === "schedules" && (
        <div>
          {schedSuccess && <div className="alert alert-success"><Icon name="check" size={14} /> {schedSuccess}</div>}
          <div className="card mb-16">
            <h3 style={{ marginBottom: 16, fontSize: "0.9rem", color: "var(--teal)", letterSpacing: "0.06em", textTransform: "uppercase" }}>
              Upload Schedule for {nurse.firstName}
            </h3>
            <div className="form-row">
              <div className="form-group">
                <label>Schedule Title *</label>
                <input placeholder="Weekly Schedule — May 12" value={schedForm.title} onChange={e => setSchedForm(f => ({ ...f, title: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Period Label *</label>
                <input placeholder="Week of May 12, 2026" value={schedForm.periodLabel} onChange={e => setSchedForm(f => ({ ...f, periodLabel: e.target.value }))} />
              </div>
            </div>
            <div className="upload-zone" style={{ cursor: uploading ? "not-allowed" : "pointer" }} onClick={() => !uploading && schedFileRef.current.click()}>
              <div className="upload-zone-icon"><Icon name="upload" size={28} /></div>
              <p><strong>{uploading ? "Uploading…" : "Click to select schedule file"}</strong></p>
              <p style={{ fontSize: "0.78rem", marginTop: 4 }}>PDF, Excel, or image — Max 25 MB</p>
              <input ref={schedFileRef} type="file" accept=".pdf,.xls,.xlsx,.jpg,.jpeg,.png" style={{ display: "none" }} onChange={handleScheduleUpload} />
            </div>
          </div>
          <div className="card">
            <h3 style={{ marginBottom: 16, fontSize: "0.9rem", color: "var(--teal)", letterSpacing: "0.06em", textTransform: "uppercase" }}>Schedule History</h3>
            {schedules.length === 0
              ? <p className="text-muted text-sm">No schedules for this nurse yet.</p>
              : <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {schedules.map(s => (
                    <div key={s.id} className="file-item">
                      <div className="file-icon"><Icon name="file" size={18} /></div>
                      <div className="file-info">
                        <div className="file-name">{s.title}</div>
                        <div className="file-meta">{s.periodLabel} · {fmtDate(s.createdAt)}</div>
                      </div>
                      <button className="btn btn-sm btn-outline" onClick={async () => {
                        try { const { url } = await Schedules.downloadUrl(s.id); window.open(url, "_blank", "noopener,noreferrer"); }
                        catch (e) { setError(e.message || "Failed to open"); }
                      }}><Icon name="eye" size={13} /> View</button>
                      <button aria-label="Delete schedule" className="btn btn-sm btn-danger" onClick={async () => {
                        try { await Schedules.remove(s.id); await loadSchedules(); }
                        catch (e) { setError(e.message || "Failed to delete"); }
                      }}><Icon name="trash" size={13} /></button>
                    </div>
                  ))}
                </div>
            }
          </div>
        </div>
      )}

      {/* COMPARATIVE TAB */}
      {activeTab === "comparative" && (
        <div>
          <p className="text-muted text-sm" style={{ marginBottom: 20 }}>Side-by-side view of contract requirements vs. this nurse's qualifications.</p>
          {interestedContracts.length === 0 ? (
            <div className="empty-state" style={{ padding: "40px 24px" }}>
              <div className="empty-state-icon"><Icon name="briefcase" size={40} /></div>
              <p>{nurse.firstName} has not expressed interest in any contracts.</p>
            </div>
          ) : interestedContracts.map(c => (
            <div key={c.id} className="card mb-16">
              <div style={{ fontWeight: 800, fontSize: "1rem", marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid var(--border)" }}>{c.title}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                {/* Contract side */}
                <div>
                  <div style={{ fontSize: "0.65rem", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--bc-teal)", marginBottom: 10 }}>Contract Requirements</div>
                  {[
                    ["Specialty", c.specialty],
                    ["Location", c.location],
                    ["Shift", c.shift],
                    ["Duration", c.duration],
                    ["Start Date", c.startDate ? fmtDate(c.startDate) : null],
                    ["Pay Rate", c.payRate],
                  ].map(([k, v]) => (
                    <div key={k} style={{ display: "flex", gap: 8, padding: "6px 0", borderBottom: "1px solid var(--border)", fontSize: "0.82rem" }}>
                      <span style={{ color: "var(--muted)", width: 90, flexShrink: 0 }}>{k}</span>
                      <span style={{ fontWeight: 600 }}>{v || <span style={{ color: "var(--muted)" }}>—</span>}</span>
                    </div>
                  ))}
                  {(c.requirements || []).length > 0 && (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--muted)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.08em" }}>Requirements</div>
                      {c.requirements.map((r, i) => (
                        <div key={i} style={{ fontSize: "0.78rem", padding: "3px 0", display: "flex", gap: 6, alignItems: "flex-start" }}>
                          <span style={{ color: "var(--bc-teal)", flexShrink: 0, marginTop: 2 }}>•</span>{r}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Nurse side */}
                <div>
                  <div style={{ fontSize: "0.65rem", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--teal)", marginBottom: 10 }}>Nurse Qualifications</div>
                  {[
                    ["Specialty", nurse.specialty, c.specialty ? nurse.specialty?.toLowerCase() === c.specialty?.toLowerCase() : null],
                    ["Province", nurse.province, c.location ? c.location.includes(nurse.province) : null],
                    ["Experience", nurse.yearsExp ? `${nurse.yearsExp} yrs` : null, null],
                    ["License #", nurse.licenseNumber, !!nurse.licenseNumber],
                    ["License Prov", nurse.licenseProvince, null],
                    ["Designation", nurse.designation, null],
                  ].map(([k, v, match]) => (
                    <div key={k} style={{ display: "flex", gap: 8, padding: "6px 0", borderBottom: "1px solid var(--border)", fontSize: "0.82rem", alignItems: "center" }}>
                      <span style={{ color: "var(--muted)", width: 90, flexShrink: 0 }}>{k}</span>
                      <span style={{ fontWeight: 600, flex: 1 }}>{v || <span style={{ color: "var(--muted)" }}>—</span>}</span>
                      {match !== null && (
                        <span style={{ width: 18, height: 18, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                          background: match ? "rgba(16,185,129,0.12)" : "rgba(220,38,38,0.09)" }}>
                          <Icon name={match ? "check" : "close"} size={10} color={match ? "#10B981" : "var(--red)"} />
                        </span>
                      )}
                    </div>
                  ))}
                  {(nurse.additionalLicences || []).length > 0 && (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--muted)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.08em" }}>Additional Licences</div>
                      {nurse.additionalLicences.map((l, i) => (
                        <div key={i} style={{ fontSize: "0.78rem", padding: "3px 0" }}>{l.number} · {l.province}{l.expiry ? ` (exp. ${l.expiry})` : ""}</div>
                      ))}
                    </div>
                  )}
                  {(nurse.additionalSpecialties || []).length > 0 && (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--muted)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.08em" }}>Additional Specialties</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {nurse.additionalSpecialties.map((s, i) => (
                          <span key={i} style={{ fontSize: "0.72rem", background: "var(--bc-teal-soft)", color: "var(--bc-teal)", borderRadius: 99, padding: "2px 8px", fontWeight: 600 }}>{s}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* EMAIL TAB */}
      {activeTab === "email" && (() => {
        const nurseName = `${nurse.firstName || ""} ${nurse.lastName || ""}`.trim();
        const fillDenialTemplate = () => {
          const contractTitle = selectedContract?.title || "[Contract Name]";
          setEmailForm({
            subject: `Re: Your Application — ${contractTitle}`,
            body: `Hi ${nurse.firstName || "there"},\n\nThank you for your interest in the ${contractTitle} position.\n\nAfter careful consideration, we regret to inform you that we are unable to move forward with your application for this contract at this time.\n\n[Add any additional context or next steps here]\n\nWe appreciate your interest in NLC Staffing and encourage you to continue checking the portal for new opportunities.\n\nWarm regards,\nYour NLC Staffing Recruiter`,
          });
          setEmailStep(0);
        };
        const fillApprovalTemplate = () => {
          const c = selectedContract;
          const contractTitle = c?.title || "[Contract Name]";
          setEmailForm({
            subject: `Contract Placement Confirmed — ${contractTitle}`,
            body: `Hi ${nurse.firstName || "there"},\n\nWe are pleased to confirm your placement for the ${contractTitle} position.\n\nContract Details:\n- Location: ${c?.location || "[Location]"}\n- Start Date: ${c?.startDate ? fmtDate(c.startDate) : "[Start Date]"}\n- Pay Rate: ${c?.payRate || "[Pay Rate]"}\n- Duration: ${c?.duration || "[Duration]"}\n\nNext Steps:\n[Add onboarding instructions, required documents, and any important dates here]\n\nPlease log in to your NLC Portal to review your upcoming schedule.\n\nWarm regards,\nYour NLC Staffing Recruiter`,
          });
          setEmailStep(0);
        };
        const sendEmail = async () => {
          setEmailSending(true);
          try {
            await Nurses.sendEmail(nurse.id, { subject: emailForm.subject, body: emailForm.body });
            setEmailSuccess("Email sent successfully.");
            setEmailForm({ subject: "", body: "" });
            setEmailStep(0);
            setTimeout(() => setEmailSuccess(""), 4000);
          } catch (e) { setError(e.message || "Failed to send email"); }
          finally { setEmailSending(false); }
        };
        return (
          <div>
            {emailSuccess && <div className="alert alert-success" style={{ marginBottom: 16 }}><Icon name="check" size={14} /> {emailSuccess}</div>}

            {/* Contract selector for templates */}
            {interestedContracts.length > 0 && (
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label>Contract context (for templates)</label>
                <select value={selectedContract?.id || ""} onChange={e => setSelectedContract(interestedContracts.find(c => c.id === e.target.value) || null)}>
                  <option value="">No contract selected</option>
                  {interestedContracts.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                </select>
              </div>
            )}

            {/* Template buttons */}
            <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
              <button className="btn btn-sm btn-outline" onClick={fillDenialTemplate}>
                Use Denial Template
              </button>
              <button className="btn btn-sm btn-outline" onClick={fillApprovalTemplate}>
                Use Approval Template
              </button>
            </div>

            {emailStep === 0 ? (
              <div>
                <div className="form-group">
                  <label>To</label>
                  <input value={nurse.email} readOnly style={{ opacity: 0.7 }} />
                </div>
                <div className="form-group">
                  <label>Subject *</label>
                  <input value={emailForm.subject} onChange={e => setEmailForm(f => ({ ...f, subject: e.target.value }))} placeholder="Email subject..." />
                </div>
                <div className="form-group">
                  <label>Message *</label>
                  <textarea rows={12} value={emailForm.body} onChange={e => setEmailForm(f => ({ ...f, body: e.target.value }))} placeholder="Write your message here..." />
                </div>
                <button
                  className="btn btn-primary"
                  disabled={!emailForm.subject.trim() || !emailForm.body.trim()}
                  onClick={() => setEmailStep(1)}
                >
                  Review Before Sending
                </button>
              </div>
            ) : (
              <div>
                <div className="card mb-16" style={{ background: "var(--navy-mid)" }}>
                  <div style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--muted)", marginBottom: 6 }}>Preview</div>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>To: {nurseName} &lt;{nurse.email}&gt;</div>
                  <div style={{ fontWeight: 600, marginBottom: 12, fontSize: "0.9rem" }}>Subject: {emailForm.subject}</div>
                  <div style={{ whiteSpace: "pre-wrap", fontSize: "0.85rem", lineHeight: 1.7, padding: "12px 16px", background: "#fff", borderRadius: 8, border: "1px solid var(--border)" }}>
                    {emailForm.body}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button className="btn btn-primary" onClick={sendEmail} disabled={emailSending}>
                    <Icon name="check" size={15} /> {emailSending ? "Sending…" : "Confirm & Send"}
                  </button>
                  <button className="btn btn-ghost" onClick={() => setEmailStep(0)} disabled={emailSending}>
                    Edit
                  </button>
                </div>
                <p className="text-muted text-sm" style={{ marginTop: 10 }}>This email will be sent to {nurse.email} on behalf of NLC Staffing.</p>
              </div>
            )}
          </div>
        );
      })()}

      {/* NOTES TAB */}
      {activeTab === "notes" && (
        <div>
          <div className="form-group">
            <label>Admin Notes for {nurse.firstName}</label>
            <textarea rows={6} value={editNotes} onChange={e => setEditNotes(e.target.value)} placeholder="Internal notes — not visible to the nurse..." />
          </div>
          <button className="btn btn-primary" onClick={saveNotes} disabled={saving}>
            <Icon name="check" size={15} /> {saving ? "Saving…" : "Save Notes"}
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================
// ADMIN: NURSE RESEARCH
// ============================================================
const SPECIALTY_WEIGHTS = {
  ICU: 1.4, Emergency: 1.35, OR: 1.3, NICU: 1.3, Cardiac: 1.25,
  Neurology: 1.2, Oncology: 1.15, Obstetrics: 1.1, Psychiatry: 1.05,
  Pediatrics: 1.05, "Med-Surg": 1.0, "Home Care": 0.95,
};

function calcExpScore(nurse) {
  const yrs = Number(nurse.yearsExp) || 0;
  const weight = SPECIALTY_WEIGHTS[nurse.specialty] || 1.0;
  const raw = Math.min(yrs * weight, 20);
  const pct = Math.round((raw / 20) * 100);
  const tier = pct >= 80 ? "Senior" : pct >= 50 ? "Intermediate" : pct >= 25 ? "Junior" : "Entry";
  return { pct, tier, yrs, weight };
}

function NurseProfileModal({ nurse: initialNurse, onClose, onUpdate }) {
  const [nurse, setNurse] = useState(initialNurse);
  const [docs, setDocs] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [designation, setDesignation] = useState(nurse.designation || "Staff");
  const [editNotes, setEditNotes] = useState(nurse.notes || "");

  useEffect(() => {
    Documents.list(nurse.id)
      .then(r => setDocs(r.documents || []))
      .catch(() => {});
    Nurses.get(nurse.id)
      .then(r => {
        setNurse(n => ({ ...n, ...r.user }));
        setDesignation(r.user.designation || "Staff");
        setEditNotes(r.user.notes || "");
      })
      .catch(() => {});
  }, [nurse.id]);

  const toggleAuth = async () => {
    const next = !nurse.contractAuthorized;
    setSaving(true);
    try {
      const res = await Nurses.authorizeContract(nurse.id, next);
      const updated = { ...nurse, ...res.user };
      setNurse(updated);
      onUpdate(updated);
    } catch (e) { setError(e.message || "Failed to update authorization"); }
    finally { setSaving(false); }
  };

  const saveDesignation = async (d) => {
    setDesignation(d);
    try {
      await Nurses.update(nurse.id, { designation: d });
      onUpdate({ ...nurse, designation: d });
    } catch (e) { setError(e.message || "Failed to save designation"); }
  };

  const saveNotes = async () => {
    setSaving(true);
    try {
      await Nurses.update(nurse.id, { notes: editNotes });
      setNurse(n => ({ ...n, notes: editNotes }));
      onUpdate({ ...nurse, notes: editNotes });
    } catch (e) { setError(e.message || "Failed to save notes"); }
    finally { setSaving(false); }
  };

  const toggleAvailability = async () => {
    const next = !nurse.contractInterestOpen;
    setSaving(true);
    try {
      await Nurses.setAvailability(nurse.id, next);
      const updated = { ...nurse, contractInterestOpen: next };
      setNurse(updated);
      onUpdate(updated);
    } catch (e) { setError(e.message || "Failed to update availability"); }
    finally { setSaving(false); }
  };

  const exp = calcExpScore(nurse);
  const name = `${nurse.firstName || ""} ${nurse.lastName || ""}`.trim() || "—";

  const tierColors = { Senior: "var(--bc-teal)", Intermediate: "var(--teal)", Junior: "var(--amber)", Entry: "var(--muted)" };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal nurse-panel" role="dialog" aria-modal="true" aria-labelledby="nurse-modal-title" style={{ maxWidth: 680 }} onClick={e => e.stopPropagation()}>
        <button className="modal-close" aria-label="Close nurse profile" onClick={onClose}><Icon name="close" size={20} /></button>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 20 }}>
          <div style={{ width: 52, height: 52, borderRadius: "50%", background: "var(--teal)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, color: "#fff", fontSize: "1.1rem", flexShrink: 0 }}>
            {name[0]?.toUpperCase() || "?"}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <h2 id="nurse-modal-title" className="modal-title" style={{ marginBottom: 0 }}>{name}</h2>
              <span className={`designation-badge ${designation === "INC" ? "designation-inc" : "designation-staff"}`}>
                {designation === "INC" ? "INC" : "Staff"}
              </span>
              <span style={{ fontSize: "0.68rem", fontWeight: 700, padding: "3px 9px", borderRadius: 99,
                background: nurse.contractAuthorized ? "rgba(0,51,102,0.07)" : "rgba(217,119,6,0.09)",
                color: nurse.contractAuthorized ? "var(--teal)" : "var(--amber)",
                border: `1px solid ${nurse.contractAuthorized ? "rgba(0,51,102,0.18)" : "rgba(217,119,6,0.22)"}` }}>
                {nurse.contractAuthorized ? "Contract Authorized" : "Pending Auth"}
              </span>
            </div>
            <p className="text-muted text-sm" style={{ marginTop: 4 }}>{nurse.email} {nurse.phone && `· ${nurse.phone}`}</p>
          </div>
        </div>

        {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

        {/* Designation toggle */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <button
            className={`btn btn-sm ${designation === "Staff" ? "btn-primary" : "btn-ghost"}`}
            onClick={() => saveDesignation("Staff")}
          >Staff</button>
          <button
            className={`btn btn-sm ${designation === "INC" ? "btn-outline" : "btn-ghost"}`}
            style={designation === "INC" ? { background: "rgba(14,116,144,0.1)", color: "var(--bc-teal)", borderColor: "rgba(14,116,144,0.3)" } : {}}
            onClick={() => saveDesignation("INC")}
          >INC (Independent Contractor)</button>
        </div>

        {/* Profile tabs */}
        <div className="profile-tab-bar" role="tablist" aria-label="Profile sections">
          {["overview", "experience", "documents", "notes"].map(t => (
            <button key={t} className={`profile-tab ${activeTab === t ? "active" : ""}`} onClick={() => setActiveTab(t)}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {/* OVERVIEW TAB */}
        {activeTab === "overview" && (
          <div>
            {[
              ["License #", nurse.licenseNumber], ["License Province", nurse.licenseProvince],
              ["Specialty", nurse.specialty], ["Province", nurse.province],
              ["GST/HST #", nurse.gstHstNumber], ["Business Name", nurse.businessName],
              ["Bank", nurse.bankName], ["Registered", fmtDate(nurse.registeredAt)],
            ].map(([k, v]) => v ? (
              <div key={k} style={{ display: "flex", borderBottom: "1px solid var(--border)", padding: "9px 0" }}>
                <span style={{ width: 160, color: "var(--muted)", fontSize: "0.8rem", flexShrink: 0 }}>{k}</span>
                <span style={{ fontSize: "0.875rem" }}>{v}</span>
              </div>
            ) : null)}

            {/* Contract authorization */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 20, padding: "16px 0", borderTop: "1px solid var(--border)" }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: "0.9rem" }}>Contract Authorization</div>
                <div className="text-sm text-muted">Allows nurse to view full contract details & apply</div>
              </div>
              <button
                onClick={toggleAuth}
                disabled={saving}
                style={{ padding: "9px 18px", borderRadius: 10, fontWeight: 700, fontSize: "0.82rem", cursor: "pointer", border: "none",
                  background: nurse.contractAuthorized ? "rgba(220,38,38,0.1)" : "var(--teal)",
                  color: nurse.contractAuthorized ? "var(--red)" : "#fff", transition: "all 0.2s", opacity: saving ? 0.6 : 1 }}
              >
                {nurse.contractAuthorized ? "Revoke Authorization" : "Approve for Contracts"}
              </button>
            </div>

            {/* Availability toggle */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 0", borderTop: "1px solid var(--border)" }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: "0.9rem" }}>Open to Contract Opportunities</div>
                <div className="text-sm text-muted">Nurse is actively available for new contracts</div>
              </div>
              <button
                onClick={toggleAvailability}
                disabled={saving}
                style={{ width: 44, height: 24, borderRadius: 99,
                  background: nurse.contractInterestOpen ? "#10B981" : "var(--border)",
                  border: "none", cursor: "pointer", position: "relative",
                  transition: "background 0.2s", flexShrink: 0, opacity: saving ? 0.5 : 1 }}
              >
                <span style={{ position: "absolute", top: 2, left: nurse.contractInterestOpen ? 22 : 2,
                  width: 20, height: 20, borderRadius: "50%", background: "#fff",
                  transition: "left 0.2s", display: "block" }} />
              </button>
            </div>

            {/* Contact */}
            {nurse.email && (
              <a
                href={`mailto:${nurse.email}`}
                className="btn btn-outline btn-full"
                style={{ marginTop: 12, textDecoration: "none" }}
              >
                <Icon name="bell" size={15} /> Contact {nurse.firstName}
              </a>
            )}
          </div>
        )}

        {/* EXPERIENCE TAB */}
        {activeTab === "experience" && (
          <div>
            <div className="card" style={{ marginBottom: 16, background: "var(--navy-mid)" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontWeight: 700 }}>Experience Score</span>
                <span style={{ fontFamily: "var(--mono)", fontSize: "1.4rem", fontWeight: 800, color: tierColors[exp.tier] }}>
                  {exp.pct}<span style={{ fontSize: "0.8rem", color: "var(--muted)" }}>/100</span>
                </span>
              </div>
              <div className="exp-bar">
                <div className="exp-bar-fill" style={{ width: `${exp.pct}%` }} />
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10 }}>
                <span style={{ fontSize: "0.8rem", color: "var(--muted)" }}>
                  {exp.yrs} yrs × {exp.weight}x specialty multiplier
                </span>
                <span style={{ fontSize: "0.75rem", fontWeight: 800, padding: "3px 10px", borderRadius: 99,
                  background: `${tierColors[exp.tier]}15`, color: tierColors[exp.tier],
                  border: `1px solid ${tierColors[exp.tier]}40` }}>
                  {exp.tier}
                </span>
              </div>
            </div>

            {/* Qualification checklist */}
            <h4 style={{ fontSize: "0.72rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 12, fontWeight: 700 }}>
              Qualification Assessment
            </h4>
            {[
              { label: "Has license number on file", done: !!nurse.licenseNumber },
              { label: "Province of license recorded", done: !!nurse.licenseProvince },
              { label: "Specialty declared", done: !!nurse.specialty },
              { label: "Years of experience entered", done: !!nurse.yearsExp },
              { label: "Banking details on file", done: !!(nurse.bankName && nurse.bankAccount) },
              { label: "SIN encrypted on file", done: !!nurse.sinMasked },
              { label: "GST/HST number (if INC)", done: designation !== "INC" || !!nurse.gstHstNumber },
              { label: "Contract authorized by admin", done: !!nurse.contractAuthorized },
              { label: "Documents uploaded", done: docs.length > 0 },
            ].map((q, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderBottom: "1px solid var(--border)" }}>
                <div style={{ width: 22, height: 22, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                  background: q.done ? "var(--teal)" : "var(--border)" }}>
                  {q.done
                    ? <Icon name="check" size={12} color="#fff" />
                    : <span style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--muted)", display: "block" }} />}
                </div>
                <span style={{ fontSize: "0.875rem", color: q.done ? "var(--white)" : "var(--muted)" }}>{q.label}</span>
              </div>
            ))}
            <div style={{ marginTop: 16, padding: "12px 16px", borderRadius: 10, background: "var(--navy-mid)", border: "1px solid var(--border)" }}>
              <div style={{ fontSize: "0.8rem", color: "var(--muted)", fontWeight: 600 }}>
                Completion: {Math.round(([!!nurse.licenseNumber, !!nurse.licenseProvince, !!nurse.specialty, !!nurse.yearsExp, !!(nurse.bankName && nurse.bankAccount), !!nurse.sinMasked, designation !== "INC" || !!nurse.gstHstNumber, !!nurse.contractAuthorized, docs.length > 0].filter(Boolean).length / 9) * 100)}%
              </div>
            </div>
          </div>
        )}

        {/* DOCUMENTS TAB */}
        {activeTab === "documents" && (
          <div>
            {docs.length === 0
              ? <div className="empty-state" style={{ padding: "40px 24px" }}><div className="empty-state-icon"><Icon name="file" size={40} /></div><p>No documents uploaded yet.</p></div>
              : docs.map(d => (
                <div key={d.id} className="file-item" style={{ marginBottom: 8 }}>
                  <div className="file-icon"><Icon name="file" size={16} /></div>
                  <div className="file-info">
                    <div className="file-name">{d.name}</div>
                    <div className="file-meta">{d.category || d.categoryKey} · {fmtDate(d.uploadedAt)}</div>
                  </div>
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={async () => {
                      try { const { url } = await Documents.downloadUrl(d.id); window.open(url, "_blank", "noopener,noreferrer"); }
                      catch (e) { setError(e.message || "Failed to fetch download URL"); }
                    }}
                  >
                    <Icon name="eye" size={13} /> View
                  </button>
                </div>
              ))
            }
          </div>
        )}

        {/* NOTES TAB */}
        {activeTab === "notes" && (
          <div>
            <div className="form-group">
              <label>Admin Notes for {nurse.firstName}</label>
              <textarea
                rows={6}
                value={editNotes}
                onChange={e => setEditNotes(e.target.value)}
                placeholder="Internal notes — not visible to the nurse..."
              />
            </div>
            <button className="btn btn-primary" onClick={saveNotes} disabled={saving}>
              <Icon name="check" size={15} /> {saving ? "Saving…" : "Save Notes"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function AdminResearch({ onOpenNurse }) {
  const [nurses, setNurses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filterSpecialty, setFilterSpecialty] = useState("");
  const [filterProvince, setFilterProvince] = useState("");
  const [filterAvail, setFilterAvail] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Nurses.list(currentPage)
      .then(r => { if (!cancelled) { setNurses(r.nurses || []); setTotal(r.total || 0); } })
      .catch(e => { if (!cancelled) setError(e.message || "Failed to load nurses"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [currentPage]);

  const specialties = [...new Set(nurses.map(n => n.specialty).filter(Boolean))].sort();
  const provinces = [...new Set(nurses.map(n => n.province).filter(Boolean))].sort();

  const filtered = nurses.filter(n => {
    const name = `${n.firstName || ""} ${n.lastName || ""} ${n.email || ""}`.toLowerCase();
    if (search && !name.includes(search.toLowerCase())) return false;
    if (filterSpecialty && n.specialty !== filterSpecialty) return false;
    if (filterProvince && n.province !== filterProvince) return false;
    if (filterAvail === "onboarded" && !(n.contractAuthorized && n.profileComplete)) return false;
    if (filterAvail === "not-boarded" && (n.contractAuthorized && n.profileComplete)) return false;
    if (filterAvail === "open" && !n.contractInterestOpen) return false;
    if (filterAvail === "closed" && n.contractInterestOpen) return false;
    if (filterAvail === "pending" && n.contractAuthorized) return false;
    return true;
  });

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Nurse Research</h1>
        <p className="page-sub">Search and manage nurse profiles, designations, and contract approvals</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Search bar */}
      <div className="search-box">
        <span className="search-box-icon"><Icon name="user" size={16} /></span>
        <input
          placeholder="Search by name or email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
        <select value={filterSpecialty} onChange={e => setFilterSpecialty(e.target.value)} style={{ maxWidth: 180, fontSize: "0.84rem" }}>
          <option value="">All specialties</option>
          {specialties.map(s => <option key={s}>{s}</option>)}
        </select>
        <select value={filterProvince} onChange={e => setFilterProvince(e.target.value)} style={{ maxWidth: 140, fontSize: "0.84rem" }}>
          <option value="">All provinces</option>
          {provinces.map(p => <option key={p}>{p}</option>)}
        </select>
        <select value={filterAvail} onChange={e => setFilterAvail(e.target.value)} style={{ maxWidth: 180, fontSize: "0.84rem" }}>
          <option value="all">All statuses</option>
          <option value="onboarded">On-Boarded (approved)</option>
          <option value="not-boarded">Not On-Boarded</option>
          <option value="open">Available now</option>
          <option value="closed">Not available</option>
          <option value="pending">Pending authorization</option>
        </select>
        <span style={{ marginLeft: "auto", alignSelf: "center", fontSize: "0.78rem", color: "var(--muted)", fontFamily: "var(--mono)" }}>
          {filtered.length} shown · {total} total
        </span>
      </div>

      {loading ? <p className="text-muted">Loading…</p> : nurses.length === 0 ? (
        <div className="empty-state"><div className="empty-state-icon"><Icon name="user" size={48} /></div><p>No nurses registered yet.</p></div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {filtered.map(n => {
            const exp = calcExpScore(n);
            const name = `${n.firstName || ""} ${n.lastName || ""}`.trim() || "—";
            return (
              <div key={n.id} className="card" style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px 20px", cursor: "pointer", transition: "all 0.15s" }}
                onClick={() => onOpenNurse(n)}
                onMouseEnter={e => e.currentTarget.style.transform = "translateY(-1px)"}
                onMouseLeave={e => e.currentTarget.style.transform = ""}
              >
                <div style={{ width: 40, height: 40, borderRadius: "50%", background: "var(--teal)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, color: "#fff", fontSize: "0.9rem", flexShrink: 0 }}>
                  {name[0]?.toUpperCase() || "?"}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: "0.92rem" }}>{name}</div>
                  <div className="text-muted text-sm">{n.specialty || "—"} · {n.province || "—"} · {n.yearsExp ? `${n.yearsExp} yrs exp` : "Exp not set"}</div>
                </div>
                {/* Exp bar mini */}
                <div style={{ width: 60, textAlign: "center" }}>
                  <div style={{ fontSize: "0.65rem", color: "var(--muted)", marginBottom: 3 }}>Exp</div>
                  <div style={{ height: 4, borderRadius: 2, background: "var(--border)" }}>
                    <div style={{ height: "100%", width: `${exp.pct}%`, background: "var(--bc-teal)", borderRadius: 2 }} />
                  </div>
                  <div style={{ fontSize: "0.62rem", color: "var(--muted)", marginTop: 2, fontFamily: "var(--mono)" }}>{exp.pct}%</div>
                </div>
                {n.designation && (
                  <span className={`designation-badge ${n.designation === "INC" ? "designation-inc" : "designation-staff"}`} style={{ fontSize: "0.62rem", padding: "2px 8px" }}>
                    {n.designation}
                  </span>
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: 4, flexShrink: 0 }}>
                  <span style={{ fontSize: "0.65rem", fontWeight: 700, padding: "2px 8px", borderRadius: 99,
                    background: n.contractAuthorized ? "rgba(0,51,102,0.07)" : "rgba(217,119,6,0.08)",
                    color: n.contractAuthorized ? "var(--teal)" : "var(--amber)",
                    border: `1px solid ${n.contractAuthorized ? "rgba(0,51,102,0.18)" : "rgba(217,119,6,0.2)"}` }}>
                    {n.contractAuthorized ? "Auth" : "Pending"}
                  </span>
                  <span style={{ fontSize: "0.65rem", fontWeight: 700, padding: "2px 8px", borderRadius: 99, textAlign: "center",
                    background: n.contractInterestOpen ? "rgba(16,185,129,0.08)" : "rgba(107,114,128,0.06)",
                    color: n.contractInterestOpen ? "#10B981" : "var(--muted)",
                    border: `1px solid ${n.contractInterestOpen ? "rgba(16,185,129,0.25)" : "rgba(107,114,128,0.15)"}` }}>
                    {n.contractInterestOpen ? "Available" : "Unavail."}
                  </span>
                </div>
                <Icon name="eye" size={16} color="var(--muted)" />
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="empty-state"><div className="empty-state-icon"><Icon name="user" size={40} /></div><p>No nurses match your filters.</p></div>
          )}
        </div>
      )}

      {/* Pagination */}
      {total > 20 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginTop: 20 }}>
          <button className="btn btn-sm btn-ghost" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)}>← Prev</button>
          <span style={{ fontSize: "0.8rem", color: "var(--muted)", fontFamily: "var(--mono)" }}>
            Page {currentPage} of {Math.ceil(total / 20)}
          </span>
          <button className="btn btn-sm btn-ghost" disabled={currentPage >= Math.ceil(total / 20)} onClick={() => setCurrentPage(p => p + 1)}>Next →</button>
        </div>
      )}

    </div>
  );
}

// ============================================================
// ADMIN: CONTRACT INTERESTS
// ============================================================
function AdminInterests({ onOpenNurse }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [removing, setRemoving] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await Interests.byContract();
      setGroups(r.contracts || []);
    } catch (e) { setError(e.message || "Failed to load interests"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleRemove = async (nurseId, contractId) => {
    setRemoving(`${nurseId}|${contractId}`);
    try {
      await Interests.adminRemove(nurseId, contractId);
      setGroups(gs => gs.map(g => g.contractId === contractId
        ? { ...g, nurses: g.nurses.filter(n => n.id !== nurseId) }
        : g
      ));
    } catch (e) {
      setError(e.message || "Failed to remove interest");
    } finally {
      setRemoving(null);
      setConfirmRemove(null);
    }
  };

  const totalInterests = groups.reduce((acc, g) => acc + (g.nurses?.length || 0), 0);

  return (
    <div>
      <div className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Contract Interests</h1>
            <p className="page-sub">Nurses who expressed interest — contact or remove as needed</p>
          </div>
          <span className="badge-pill">{totalInterests} total</span>
        </div>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      {loading && <p className="text-muted">Loading…</p>}
      {!loading && groups.every(g => !g.nurses?.length) && (
        <div className="empty-state"><div className="empty-state-icon"><Icon name="bell" size={48} /></div><p>No expressions of interest yet.</p></div>
      )}

      {groups.filter(c => c.nurses?.length > 0).map(c => (
        <div key={c.contractId} className="card mb-16">
          <div className="flex items-center justify-between mb-16">
            <div>
              <div style={{ fontWeight: 800, fontSize: "1rem" }}>{c.title}</div>
              <div className="text-muted text-sm" style={{ marginTop: 3 }}>
                {[c.location, c.payRate].filter(Boolean).join(" · ")}
              </div>
            </div>
            <span className="badge-pill">{c.nurses.length} interested</span>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {c.nurses.map(n => (
              <div key={n.id} className="interest-nurse-row" style={{ cursor: "pointer" }} onClick={() => onOpenNurse && onOpenNurse(n)}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--teal)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, color: "#fff", fontSize: "0.8rem", flexShrink: 0 }}>
                  {(n.firstName?.[0] || "?").toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: "0.88rem" }}>{n.firstName} {n.lastName}</div>
                  <div className="text-muted text-sm">{n.specialty || "—"} · {n.province || "—"}</div>
                </div>
                <div style={{ display: "flex", gap: 8, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                  {n.email && (
                    <a
                      href={`mailto:${n.email}?subject=Re: ${encodeURIComponent(c.title)}&body=Hi ${encodeURIComponent(n.firstName || "")},`}
                      className="btn btn-sm btn-outline"
                      style={{ textDecoration: "none" }}
                    >
                      <Icon name="bell" size={13} /> Email
                    </a>
                  )}
                  {confirmRemove === `${n.id}|${c.contractId}` ? (
                    <div style={{ display: "flex", gap: 6 }}>
                      <button className="btn btn-sm btn-danger" disabled={removing === `${n.id}|${c.contractId}`}
                        onClick={() => handleRemove(n.id, c.contractId)}>
                        {removing === `${n.id}|${c.contractId}` ? "…" : "Confirm"}
                      </button>
                      <button className="btn btn-sm btn-ghost" onClick={() => setConfirmRemove(null)}>Cancel</button>
                    </div>
                  ) : (
                    <button className="btn btn-sm btn-ghost" onClick={() => setConfirmRemove(`${n.id}|${c.contractId}`)}>
                      <Icon name="trash" size={13} /> Remove
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Confirm remove modal */}
      {confirmRemove && (
        <div style={{ position: "fixed", bottom: 24, right: 24, background: "var(--card)", border: "1px solid var(--border)", borderRadius: 14, padding: "16px 20px", boxShadow: "var(--shadow-lg)", zIndex: 300, maxWidth: 320 }}>
          <p style={{ fontWeight: 600, marginBottom: 12, fontSize: "0.88rem" }}>Remove this interest expression?</p>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-sm btn-danger" onClick={() => {
              const [nId, cId] = confirmRemove.split("|");
              handleRemove(nId, cId);
            }}>Remove</button>
            <button className="btn btn-sm btn-ghost" onClick={() => setConfirmRemove(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// ADMIN: ADD CONTRACT
// ============================================================
function AdminAddContract() {
  const FIELDS = ["title","location","specialty","shift","duration","startDate","endDate","payRate","description","requirements"];
  const [form, setForm] = useState({ title: "", location: "", duration: "", startDate: "", endDate: "", payRate: "", specialty: "", shift: "", description: "", requirements: "", urgent: false });
  const [intentionallyBlank, setIntentionallyBlank] = useState({});
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmStep, setConfirmStep] = useState(0);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const FIELD_LABELS = { title: "Contract Title", location: "Location", specialty: "Specialty", shift: "Shift", duration: "Duration", startDate: "Start Date", endDate: "End Date", payRate: "Pay Rate", description: "Description", requirements: "Requirements" };
  const emptyFields = FIELDS.filter(k => !form[k] && !intentionallyBlank[k]);

  const handlePostClick = () => {
    if (emptyFields.length > 0) { setConfirmStep(1); return; }
    setConfirmStep(2);
  };

  const save = async () => {
    setBusy(true); setError("");
    try {
      await Contracts.create({
        ...form,
        requirements: form.requirements.split("\n").map(s => s.trim()).filter(Boolean),
        hiddenFields: Object.keys(intentionallyBlank),
      });
      setSaved(true);
      setForm({ title: "", location: "", duration: "", startDate: "", endDate: "", payRate: "", specialty: "", shift: "", description: "", requirements: "", urgent: false });
      setIntentionallyBlank({});
      setConfirmStep(0);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e.message || "Failed to post contract");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Post New Contract</h1>
        <p className="page-sub">Contracts will appear on the nurse-facing board immediately after two confirmations</p>
      </div>
      {saved && <div className="alert alert-success"><Icon name="check" size={14} /> Contract posted successfully.</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {/* Step 1: Confirm blank fields */}
      {confirmStep === 1 && (
        <div className="card mb-16" style={{ border: "1.5px solid var(--amber)" }}>
          <h3 style={{ marginBottom: 8, fontSize: "0.9rem", color: "var(--amber)" }}>Some fields are empty</h3>
          <p className="text-sm text-muted mb-16">The following fields were left blank. Confirm which ones are intentionally empty — nurses will not see intentionally blank fields.</p>
          {emptyFields.map(k => (
            <div key={k} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
              <input type="checkbox" id={`blank-${k}`} checked={!!intentionallyBlank[k]} onChange={e => setIntentionallyBlank(b => ({ ...b, [k]: e.target.checked }))} style={{ width: "auto", flexShrink: 0 }} />
              <label htmlFor={`blank-${k}`} style={{ marginBottom: 0, textTransform: "none", letterSpacing: 0, fontSize: "0.88rem", color: "var(--white)", cursor: "pointer" }}>
                <strong>{FIELD_LABELS[k]}</strong> — intentionally left blank
              </label>
            </div>
          ))}
          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <button className="btn btn-sm btn-primary" onClick={() => setConfirmStep(2)}>Continue to Final Confirmation</button>
            <button className="btn btn-sm btn-ghost" onClick={() => setConfirmStep(0)}>Back to Edit</button>
          </div>
        </div>
      )}

      {/* Step 2: Final confirmation */}
      {confirmStep === 2 && (
        <div className="card mb-16" style={{ border: "1.5px solid var(--teal)" }}>
          <h3 style={{ marginBottom: 8, fontSize: "0.9rem", color: "var(--teal)" }}>Ready to go live?</h3>
          <p className="text-sm text-muted mb-16">This contract will become visible to all nurses immediately. This is your final confirmation.</p>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="btn btn-primary" onClick={save} disabled={busy}><Icon name="check" size={14} /> {busy ? "Posting…" : "Confirm — Post Contract"}</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setConfirmStep(0)}>Cancel</button>
          </div>
        </div>
      )}

      <div className="card">
        <div className="form-row">
          <div className="form-group"><label>Contract Title *</label><input placeholder="ICU Nurse – Vancouver General" value={form.title} onChange={e => set("title", e.target.value)} /></div>
          <div className="form-group"><label>Location *</label><input placeholder="Vancouver, BC" value={form.location} onChange={e => set("location", e.target.value)} /></div>
        </div>
        <div className="form-row">
          <div className="form-group"><label>Specialty</label>
            <select value={form.specialty} onChange={e => set("specialty", e.target.value)}>
              <option value="">Select...</option>
              {["ICU","Emergency","OR","Pediatrics","Oncology","Med-Surg","Cardiac","NICU","Obstetrics","Psychiatry","Other"].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="form-group"><label>Shift</label><input placeholder="Days / Nights / Rotating" value={form.shift} onChange={e => set("shift", e.target.value)} /></div>
        </div>
        <div className="form-row">
          <div className="form-group"><label>Duration</label><input placeholder="13 weeks" value={form.duration} onChange={e => set("duration", e.target.value)} /></div>
          <div className="form-group"><label>Start Date</label><input type="date" value={form.startDate} onChange={e => set("startDate", e.target.value)} /></div>
          <div className="form-group"><label>End Date</label><input type="date" value={form.endDate} onChange={e => set("endDate", e.target.value)} /></div>
          <div className="form-group"><label>Pay Rate</label><input placeholder="$65–$75/hr" value={form.payRate} onChange={e => set("payRate", e.target.value)} /></div>
        </div>
        <div className="form-group"><label>Description</label><textarea rows={4} value={form.description} onChange={e => set("description", e.target.value)} placeholder="Contract details..." /></div>
        <div className="form-group"><label>Requirements (one per line)</label><textarea rows={4} value={form.requirements} onChange={e => set("requirements", e.target.value)} placeholder={"RN License (BC)\n2+ yrs ICU experience\nBLS/ACLS certified"} /></div>
        <div className="form-group" style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <input type="checkbox" id="urgent" checked={form.urgent} onChange={e => set("urgent", e.target.checked)} style={{ width: "auto" }} />
          <label htmlFor="urgent" style={{ marginBottom: 0, textTransform: "none", letterSpacing: 0, fontSize: "0.88rem", color: "var(--white)" }}>Mark as Urgent</label>
        </div>
        {confirmStep === 0 && (
          <button className="btn btn-primary" onClick={handlePostClick}><Icon name="plus" size={16} /> Post Contract</button>
        )}
      </div>
    </div>
  );
}

// ============================================================
// ADMIN: SCHEDULE UPLOAD
// ============================================================
function AdminSchedules() {
  const [schedules, setSchedules] = useState([]);
  const [form, setForm] = useState({ title: "", periodLabel: "" });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const fileRef = useRef();

  const refresh = useCallback(async () => {
    const r = await Schedules.list();
    setSchedules(r.schedules || []);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!form.title || !form.periodLabel) { setError("Title and period label are required."); return; }
    setUploading(true); setError("");
    try {
      await Schedules.upload(file, form.title, form.periodLabel);
      setForm({ title: "", periodLabel: "" });
      setSuccess("Schedule uploaded and nurses notified via email.");
      setTimeout(() => setSuccess(""), 4000);
      await refresh();
    } catch (e) {
      setError(e.message || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeSchedule = async (id) => {
    try { await Schedules.remove(id); await refresh(); }
    catch (e) { setError(e.message || "Failed to delete"); }
  };

  const open = async (id) => {
    try {
      const { url } = await Schedules.downloadUrl(id);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) { setError(e.message || "Failed to open"); }
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Schedules</h1>
        <p className="page-sub">Upload and manage nurse schedules — nurses will be notified by email</p>
      </div>
      {success && <div className="alert alert-success"><Icon name="check" size={14} /> {success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="card mb-16">
        <h3 style={{ marginBottom: 16, fontSize: "0.9rem", color: "var(--teal)", letterSpacing: "0.06em", textTransform: "uppercase" }}>Upload New Schedule</h3>
        <div className="form-row">
          <div className="form-group"><label>Schedule Title *</label><input placeholder="Weekly Schedule — May 12" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} /></div>
          <div className="form-group"><label>Period Label *</label><input placeholder="Week of May 12, 2026" value={form.periodLabel} onChange={e => setForm(f => ({ ...f, periodLabel: e.target.value }))} /></div>
        </div>
        <div
          className="upload-zone"
          style={{ cursor: uploading ? "not-allowed" : "pointer" }}
          onClick={() => !uploading && fileRef.current.click()}
        >
          <div className="upload-zone-icon"><Icon name="upload" size={32} /></div>
          <p><strong>{uploading ? "Uploading…" : "Click to select schedule file"}</strong></p>
          <p style={{ fontSize: "0.78rem", marginTop: 4 }}>PDF, Excel, or image — Max 25 MB</p>
          <input ref={fileRef} type="file" accept=".pdf,.xls,.xlsx,.jpg,.jpeg,.png" style={{ display: "none" }} onChange={handleUpload} />
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginBottom: 16, fontSize: "0.9rem", color: "var(--teal)", letterSpacing: "0.06em", textTransform: "uppercase" }}>Schedule History</h3>
        {schedules.length === 0
          ? <p className="text-muted text-sm">No schedules uploaded yet.</p>
          : <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {schedules.map(s => (
                <div key={s.id} className="file-item">
                  <div className="file-icon"><Icon name="file" size={18} /></div>
                  <div className="file-info">
                    <div className="file-name">{s.title}</div>
                    <div className="file-meta">{s.periodLabel} · Posted {fmtDate(s.createdAt)}</div>
                  </div>
                  <button className="btn btn-sm btn-outline" onClick={() => open(s.id)}><Icon name="eye" size={13} /> View</button>
                  <button aria-label="Remove schedule" className="btn btn-sm btn-danger" onClick={() => removeSchedule(s.id)}><Icon name="trash" size={13} /></button>
                </div>
              ))}
            </div>
        }
      </div>
    </div>
  );
}

// ============================================================
// ADMIN: CATEGORY DESCRIPTIONS
// ============================================================
function AdminCategories() {
  const [categories, setCats] = useState([]);
  const [editing, setEditing] = useState({});
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    Categories.list()
      .then(r => {
        setCats(r.categories || []);
        const init = {};
        (r.categories || []).forEach(c => { init[c.key] = { label: c.label, description: c.description }; });
        setEditing(init);
      })
      .catch(e => setError(e.message || "Failed to load categories"));
  }, []);

  const save = async (key) => {
    try {
      await Categories.update(key, editing[key]);
      setCats(cs => cs.map(c => c.key === key ? { ...c, ...editing[key] } : c));
      setSaved(key);
      setTimeout(() => setSaved(null), 2000);
    } catch (e) { setError(e.message || "Failed to save"); }
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Document Categories</h1>
        <p className="page-sub">Edit the label and instructions nurses see for each document category</p>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {categories.map(cat => (
          <div key={cat.key} className="card">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <span className="badge-pill">{cat.key}</span>
              {saved === cat.key && <span style={{ color: "var(--teal)", fontSize: "0.8rem", fontWeight: 600 }}><Icon name="check" size={12} /> Saved</span>}
            </div>
            <div className="form-row">
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Label</label>
                <input value={editing[cat.key]?.label || ""} onChange={e => setEditing(ed => ({ ...ed, [cat.key]: { ...ed[cat.key], label: e.target.value } }))} />
              </div>
            </div>
            <div className="form-group" style={{ marginTop: 10, marginBottom: 10 }}>
              <label>Description (shown to nurses)</label>
              <textarea rows={2} value={editing[cat.key]?.description || ""} onChange={e => setEditing(ed => ({ ...ed, [cat.key]: { ...ed[cat.key], description: e.target.value } }))} />
            </div>
            <button className="btn btn-sm btn-primary" onClick={() => save(cat.key)}><Icon name="check" size={14} /> Save</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// ADMIN: NURSE AVAILABILITY
// ============================================================
function AdminAvailability() {
  const [nurses, setNurses] = useState([]);
  const [savingAvail, setSavingAvail] = useState(null);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const r = await Nurses.list();
      setNurses(r.nurses || []);
    } catch (e) { setError(e.message || "Failed to load"); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const toggleAvail = async (nurse) => {
    const next = !nurse.contractInterestOpen;
    setSavingAvail(nurse.id);
    setNurses(ns => ns.map(n => n.id === nurse.id ? { ...n, contractInterestOpen: next } : n));
    try {
      await Nurses.setAvailability(nurse.id, next);
    } catch {
      setNurses(ns => ns.map(n => n.id === nurse.id ? { ...n, contractInterestOpen: !next } : n));
    } finally { setSavingAvail(null); }
  };

  const available = nurses.filter(n => n.contractInterestOpen).length;

  return (
    <div>
      <div className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Nurse Availability</h1>
            <p className="page-sub">Nurses open to contract opportunities — toggle to update their status</p>
          </div>
          <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "#10B981" }}>{available} / {nurses.length} available</span>
        </div>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      <div style={{ height: 6, background: "var(--border)", borderRadius: 3, marginBottom: 20 }}>
        <div style={{ height: "100%", width: `${nurses.length ? (available / nurses.length) * 100 : 0}%`, background: "linear-gradient(90deg, #10B981, #34D399)", borderRadius: 3, transition: "width 0.5s" }} />
      </div>
      <div className="card">
        {nurses.length === 0
          ? <p className="text-muted text-sm">No nurses registered yet.</p>
          : nurses.map(n => (
            <div key={n.id} className="avail-row">
              <div className={`avail-dot ${n.contractInterestOpen ? "open" : "closed"}`} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: "0.88rem" }}>
                  {n.name || `${n.firstName || ""} ${n.lastName || ""}`.trim() || "—"}
                </div>
                <div className="text-muted text-sm">{n.specialty || "Specialty not set"} · {n.province || "—"}</div>
              </div>
              <span style={{ fontSize: "0.68rem", fontWeight: 700, padding: "2px 8px", borderRadius: 99,
                background: n.contractAuthorized ? "rgba(0,51,102,0.07)" : "rgba(217,119,6,0.08)",
                color: n.contractAuthorized ? "var(--teal)" : "var(--amber)",
                border: `1px solid ${n.contractAuthorized ? "rgba(0,51,102,0.18)" : "rgba(217,119,6,0.2)"}` }}>
                {n.contractAuthorized ? "Authorized" : "Pending"}
              </span>
              <button
                disabled={savingAvail === n.id}
                onClick={() => toggleAvail(n)}
                style={{ width: 44, height: 24, borderRadius: 99, background: n.contractInterestOpen ? "#10B981" : "var(--border)", border: "none", cursor: "pointer", position: "relative", transition: "background 0.2s", flexShrink: 0, opacity: savingAvail === n.id ? 0.5 : 1 }}
              >
                <span style={{ position: "absolute", top: 2, left: n.contractInterestOpen ? 22 : 2, width: 20, height: 20, borderRadius: "50%", background: "#fff", transition: "left 0.2s", display: "block" }} />
              </button>
            </div>
          ))
        }
      </div>
    </div>
  );
}

// ============================================================
// ADMIN: POSTED CONTRACTS
// ============================================================
function AdminPostedContracts({ onOpenNurse }) {
  const [contracts, setContracts] = useState([]);
  const [groups, setGroups] = useState({});
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filterLoc, setFilterLoc] = useState("");
  const [filterSpec, setFilterSpec] = useState("");
  const [viewData, setViewData] = useState({});

  useEffect(() => {
    let cancelled = false;
    Promise.all([Contracts.list(), Interests.byContract()])
      .then(([c, i]) => {
        if (cancelled) return;
        setContracts(c.contracts || []);
        const g = {};
        (i.contracts || []).forEach(ic => { g[ic.contractId] = ic.nurses || []; });
        setGroups(g);
      })
      .catch(e => setError(e.message || "Failed to load"))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const filtered = contracts.filter(c => {
    if (filterLoc && !(c.location || "").toLowerCase().includes(filterLoc.toLowerCase())) return false;
    if (filterSpec && c.specialty !== filterSpec) return false;
    return true;
  });

  const specialties = [...new Set(contracts.map(c => c.specialty).filter(Boolean))];

  const handleSelect = (c) => {
    const next = selected?.id === c.id ? null : c;
    setSelected(next);
    if (next && !viewData[next.id]) {
      setViewData(prev => ({ ...prev, [next.id]: { viewers: [], totalViews: 0, loading: true } }));
      Contracts.views(next.id)
        .then(d => setViewData(prev => ({ ...prev, [next.id]: { viewers: d.viewers || [], totalViews: d.totalViews || 0, loading: false } })))
        .catch(() => setViewData(prev => ({ ...prev, [next.id]: { viewers: [], totalViews: 0, loading: false } })));
    }
  };

  return (
    <div>
      <div className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Posted Contracts</h1>
            <p className="page-sub">All live contracts — click to see interested nurses and analytics</p>
          </div>
          <span className="badge-pill">{contracts.length} live</span>
        </div>
      </div>
      {error && <div className="alert alert-error">{error}</div>}

      <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
        <input style={{ maxWidth: 240 }} placeholder="Filter by location..." value={filterLoc} onChange={e => setFilterLoc(e.target.value)} />
        <select style={{ maxWidth: 160, fontSize: "0.84rem" }} value={filterSpec} onChange={e => setFilterSpec(e.target.value)}>
          <option value="">All specialties</option>
          {specialties.map(s => <option key={s}>{s}</option>)}
        </select>
      </div>

      {loading ? <p className="text-muted">Loading…</p> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {filtered.map(c => {
            const interested = groups[c.id] || [];
            const uniqueViewers = [...new Map(interested.map(n => [n.id, n])).values()];
            return (
              <div key={c.id} className="card" style={{ cursor: "pointer" }} onClick={() => handleSelect(c)}>
                <div className="flex items-center justify-between">
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 800, fontSize: "1rem" }}>{c.title}</div>
                    <div className="text-muted text-sm" style={{ marginTop: 3 }}>{[c.location, c.specialty, c.payRate].filter(Boolean).join(" · ")}</div>
                  </div>
                  <div style={{ display: "flex", gap: 10, alignItems: "center", flexShrink: 0 }}>
                    {c.urgent && <span className="tag tag-urgent">Urgent</span>}
                    <span className="badge-pill">{uniqueViewers.length} interested</span>
                  </div>
                </div>
                {selected?.id === c.id && (
                  <div style={{ marginTop: 16, borderTop: "1px solid var(--border)", paddingTop: 16, display: "flex", gap: 24, flexWrap: "wrap" }}>
                    {/* Interested nurses */}
                    <div style={{ flex: 1, minWidth: 220 }}>
                      <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "var(--muted)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>Interested ({uniqueViewers.length})</div>
                      {uniqueViewers.length === 0 ? (
                        <p className="text-muted text-sm">No interest yet.</p>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          {uniqueViewers.map(n => (
                            <div key={n.id} className="interest-nurse-row" style={{ cursor: "pointer" }} onClick={e => { e.stopPropagation(); onOpenNurse && onOpenNurse(n); }}>
                              <div style={{ width: 32, height: 32, borderRadius: "50%", background: "var(--teal)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, color: "#fff", fontSize: "0.75rem", flexShrink: 0 }}>
                                {(n.firstName?.[0] || "?").toUpperCase()}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 700, fontSize: "0.85rem" }}>{n.firstName} {n.lastName}</div>
                                <div className="text-muted text-sm">{n.specialty || "—"} · {n.province || "—"}</div>
                              </div>
                              <Icon name="eye" size={14} color="var(--muted)" />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* View analytics */}
                    <div style={{ flex: 1, minWidth: 220, borderLeft: "1px solid var(--border)", paddingLeft: 20 }}>
                      <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "var(--muted)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>
                        Viewers{viewData[c.id]?.loading ? " …" : ` (${(viewData[c.id]?.viewers || []).length} unique · ${viewData[c.id]?.totalViews ?? 0} total)`}
                      </div>
                      {viewData[c.id]?.loading ? (
                        <p className="text-muted text-sm">Loading…</p>
                      ) : !(viewData[c.id]?.viewers?.length) ? (
                        <p className="text-muted text-sm">No views recorded yet.</p>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          {viewData[c.id].viewers.map(v => (
                            <div key={v.id} className="interest-nurse-row" style={{ cursor: "pointer" }} onClick={e => { e.stopPropagation(); onOpenNurse && onOpenNurse(v); }}>
                              <div style={{ width: 32, height: 32, borderRadius: "50%", background: "var(--teal)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, color: "#fff", fontSize: "0.75rem", flexShrink: 0 }}>
                                {(v.firstName?.[0] || "?").toUpperCase()}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 700, fontSize: "0.85rem" }}>{v.firstName} {v.lastName}</div>
                                <div className="text-muted text-sm">{v.specialty || "—"} · {v.province || "—"}</div>
                              </div>
                              <span style={{ fontSize: "0.75rem", color: "var(--muted)", flexShrink: 0 }}>{v.viewCount}×</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {filtered.length === 0 && !loading && (
            <div className="empty-state"><div className="empty-state-icon"><Icon name="briefcase" size={48} /></div><p>No contracts posted yet.</p></div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// MESSAGES (both roles)
// ============================================================
function Messages({ user }) {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [nursePickerOpen, setNursePickerOpen] = useState(false);
  const [nurseSearch, setNurseSearch] = useState("");
  const [nurseList, setNurseList] = useState([]);
  const endRef = useRef(null);

  const loadThreads = useCallback(async () => {
    try {
      const r = await MessagesAPI.threads();
      setThreads(r.threads || []);
    } catch {}
  }, []);

  const loadMessages = useCallback(async (threadId) => {
    try {
      const r = await MessagesAPI.list(threadId);
      setMsgs(r.messages || []);
    } catch {}
  }, []);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  useEffect(() => {
    if (active) loadMessages(active.id);
  }, [active, loadMessages]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs]);

  useEffect(() => {
    const id = setInterval(() => {
      if (active) loadMessages(active.id);
      loadThreads();
    }, 5000);
    return () => clearInterval(id);
  }, [active, loadMessages, loadThreads]);

  useEffect(() => {
    if (nursePickerOpen && user.role === "admin") {
      Nurses.list().then(r => setNurseList(r.nurses || [])).catch(() => {});
    }
  }, [nursePickerOpen, user.role]);

  const send = async () => {
    if (!draft.trim() || !active) return;
    setSending(true);
    try {
      await MessagesAPI.send(active.id, draft.trim());
      setDraft("");
      await loadMessages(active.id);
      await loadThreads();
    } catch (e) { setError(e.message || "Failed to send"); }
    finally { setSending(false); }
  };

  const startThread = async (nurseId) => {
    try {
      const r = await MessagesAPI.newThread(nurseId);
      await loadThreads();
      if (r.thread) setActive(r.thread);
      setNursePickerOpen(false);
      setNurseSearch("");
    } catch (e) { setError(e.message || "Failed to start conversation"); }
  };

  return (
    <div style={{ display: "flex", gap: 0, height: "calc(100vh - 200px)", minHeight: 400 }}>
      {/* Thread list */}
      <div style={{ width: 260, flexShrink: 0, borderRight: "1px solid var(--border)", overflowY: "auto" }}>
        <div style={{ padding: "16px 14px", borderBottom: "1px solid var(--border)", fontWeight: 700, fontSize: "0.85rem" }}>
          {user.role === "admin" ? "Nurse Conversations" : "Messages"}
        </div>
        {threads.length === 0 && <p className="text-muted text-sm" style={{ padding: "16px 14px" }}>{user.role === "admin" ? "No conversations yet." : "No messages yet. Your recruiter will reach out here."}</p>}
        {threads.map(t => (
          <div key={t.id} onClick={() => setActive(t)} style={{ padding: "12px 14px", cursor: "pointer", borderBottom: "1px solid var(--border)", background: active?.id === t.id ? "rgba(0,51,102,0.05)" : "transparent", borderLeft: active?.id === t.id ? "3px solid var(--teal)" : "3px solid transparent" }}>
            <div style={{ fontWeight: 600, fontSize: "0.85rem" }}>{t.otherName || "Unknown"}</div>
            <div className="text-sm text-muted" style={{ marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.lastMessage || "No messages yet"}</div>
            {t.unread > 0 && <span style={{ fontSize: "0.65rem", fontWeight: 800, background: "var(--teal)", color: "#fff", borderRadius: 99, padding: "1px 7px", marginTop: 4, display: "inline-block" }}>{t.unread}</span>}
          </div>
        ))}
        {user.role === "admin" && (
          <button className="btn btn-sm btn-ghost" style={{ margin: "12px 14px", width: "calc(100% - 28px)" }} onClick={() => setNursePickerOpen(true)}>
            <Icon name="plus" size={13} /> New Conversation
          </button>
        )}

        {/* Nurse picker modal */}
        {nursePickerOpen && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ background: "var(--card)", borderRadius: 16, padding: 24, width: 400, maxWidth: "90vw", boxShadow: "var(--shadow-lg)" }}>
              <div style={{ fontWeight: 700, marginBottom: 14 }}>Start New Conversation</div>
              <input placeholder="Search nurses..." value={nurseSearch} onChange={e => setNurseSearch(e.target.value)} style={{ marginBottom: 12 }} autoFocus />
              <div style={{ maxHeight: 280, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
                {nurseList.filter(n => {
                  const q = nurseSearch.toLowerCase();
                  return !q || `${n.firstName} ${n.lastName} ${n.email}`.toLowerCase().includes(q);
                }).map(n => (
                  <button key={n.id} className="btn btn-ghost" style={{ justifyContent: "flex-start", textAlign: "left" }} onClick={() => startThread(n.id)}>
                    <div style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--teal)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, color: "#fff", fontSize: "0.72rem", flexShrink: 0, marginRight: 10 }}>
                      {n.firstName?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: "0.85rem" }}>{n.firstName} {n.lastName}</div>
                      <div style={{ fontSize: "0.72rem", color: "var(--muted)" }}>{n.email}</div>
                    </div>
                  </button>
                ))}
                {nurseList.length === 0 && <p className="text-muted text-sm" style={{ padding: "8px 0" }}>Loading nurses…</p>}
              </div>
              <button className="btn btn-ghost btn-sm" style={{ marginTop: 12, width: "100%" }} onClick={() => { setNursePickerOpen(false); setNurseSearch(""); }}>Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* Message pane */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {!active ? (
          <div className="empty-state" style={{ margin: "auto" }}>
            <div className="empty-state-icon"><Icon name="bell" size={40} /></div>
            <p>Select a conversation</p>
          </div>
        ) : (
          <>
            <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)", fontWeight: 700 }}>{active.otherName}</div>
            <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 10 }}>
              {msgs.map(m => {
                const mine = m.senderId === user.id;
                return (
                  <div key={m.id} style={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start" }}>
                    <div style={{ maxWidth: "70%", background: mine ? "var(--teal)" : "var(--navy-mid)", color: mine ? "#fff" : "var(--white)", borderRadius: mine ? "16px 16px 4px 16px" : "16px 16px 16px 4px", padding: "10px 14px", fontSize: "0.88rem", lineHeight: 1.5 }}>
                      <div>{m.body}</div>
                      <div style={{ fontSize: "0.65rem", opacity: 0.65, marginTop: 4, textAlign: mine ? "right" : "left" }}>{fmtDate(m.createdAt)}</div>
                    </div>
                  </div>
                );
              })}
              <div ref={endRef} />
            </div>
            {error && <div className="alert alert-error" style={{ margin: "0 20px" }}>{error}</div>}
            <div style={{ padding: "12px 20px", borderTop: "1px solid var(--border)", display: "flex", gap: 10 }}>
              <input style={{ flex: 1 }} placeholder="Type a message…" value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())} />
              <button className="btn btn-primary btn-sm" onClick={send} disabled={sending || !draft.trim()}>
                {sending ? "…" : "Send"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================
// ADMIN: NOTIFICATION SETTINGS
// ============================================================
function AdminNotifications() {
  const [notifSettings, setNotifSettings] = useState([]);
  const [nurses, setNurses] = useState([]);
  const [payrollMsg, setPayrollMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([Nurses.list(), Notifications.allSettings()])
      .then(([n, ns]) => {
        const nurseList = (n.nurses || []).filter(x => x.contractInterestOpen || x.contractAuthorized);
        setNurses(nurseList);
        setNotifSettings(ns.settings || []);
      })
      .catch(e => setError(e.message || "Failed to load"));
  }, []);

  const sendPayrollEmail = async () => {
    if (!payrollMsg.trim()) { setError("Please enter a message before sending."); return; }
    setSending(true); setError("");
    try {
      await Notifications.sendMassEmail({ type: "payroll_reminder", message: payrollMsg });
      setSent(true);
      setPayrollMsg("");
      setTimeout(() => setSent(false), 3000);
    } catch (e) { setError(e.message || "Failed to send"); }
    finally { setSending(false); }
  };

  const onContractNurses = notifSettings.filter(n => n.new_contract_alert);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Notifications</h1>
        <p className="page-sub">Send mass emails and manage nurse communication</p>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      {sent && <div className="alert alert-success"><Icon name="check" size={14} /> Payroll reminder sent.</div>}

      {/* Mass payroll email */}
      <div className="card mb-16">
        <h3 style={{ marginBottom: 4, fontSize: "0.9rem", color: "var(--teal)", letterSpacing: "0.06em", textTransform: "uppercase" }}>Payroll Reminder</h3>
        <p className="text-sm text-muted mb-16">Sends to all nurses currently on contract. They must have payroll reminders enabled in their notification settings.</p>
        <div className="form-group">
          <label>Message</label>
          <textarea rows={4} value={payrollMsg} onChange={e => setPayrollMsg(e.target.value)} placeholder="Reminder: please submit your timesheet for the week ending..." />
        </div>
        <button className="btn btn-primary" onClick={sendPayrollEmail} disabled={sending}>
          <Icon name="bell" size={14} /> {sending ? "Sending…" : `Send to ${nurses.length} nurses on contract`}
        </button>
      </div>

      {/* New contract alert opt-in list */}
      <div className="card">
        <h3 style={{ marginBottom: 4, fontSize: "0.9rem", color: "var(--teal)", letterSpacing: "0.06em", textTransform: "uppercase" }}>New Contract Alert Opt-Ins</h3>
        <p className="text-sm text-muted mb-16">Nurses who have opted in to receive an email when new contracts are posted.</p>
        {onContractNurses.length === 0 ? (
          <p className="text-muted text-sm">No nurses have opted in yet.</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Nurse</th><th>Email</th><th>Specialty</th></tr></thead>
              <tbody>
                {onContractNurses.map(n => (
                  <tr key={n.id}>
                    <td style={{ fontWeight: 600 }}>{n.firstName || n.first_name} {n.lastName || n.last_name}</td>
                    <td className="text-muted">{n.email}</td>
                    <td className="text-muted">{n.specialty || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// ADMIN: ACCESS CODES
// ============================================================
function AdminAccessCodes() {
  const [codes, setCodes]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError]       = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [revoking, setRevoking] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await AccessCodes.list();
      setCodes(data.codes || []);
    } catch (e) {
      setError(e.message || "Failed to load codes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    setError("");
    try {
      const code = await AccessCodes.create();
      setCodes(prev => [code, ...prev]);
    } catch (e) {
      setError(e.message || "Failed to generate code");
    } finally {
      setGenerating(false);
    }
  };

  const copy = (code, id) => {
    navigator.clipboard.writeText(code).catch(() => {});
    setCopiedId(id);
    setTimeout(() => setCopiedId(c => c === id ? null : c), 2000);
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this access code? It will no longer be usable.")) return;
    setRevoking(id);
    try {
      await AccessCodes.revoke(id);
      setCodes(prev => prev.filter(c => c.id !== id));
    } catch (e) {
      setError(e.message || "Failed to revoke code");
    } finally {
      setRevoking(null);
    }
  };

  const fmtTs = (ts) => {
    if (!ts) return "—";
    return new Date(ts).toLocaleString("en-CA", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const statusStyle = (status) => {
    if (status === "active")  return { bg: "rgba(22,163,74,0.1)",  color: "#16A34A" };
    if (status === "used")    return { bg: "rgba(14,116,144,0.1)", color: "#0E7490" };
    return                           { bg: "rgba(107,114,128,0.1)", color: "#6B7280" };
  };

  const active  = codes.filter(c => c.status === "active");
  const rest    = codes.filter(c => c.status !== "active");

  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 24 }}>
        <div>
          <h2 style={{ fontSize: "1.35rem", fontWeight: 800, color: "var(--teal)", marginBottom: 4 }}>Access Codes</h2>
          <p style={{ color: "var(--muted)", fontSize: "0.875rem" }}>
            Generate single-use invite codes for new nurse registrations. Each code expires in 72 hours.
          </p>
        </div>
        <button className="btn btn-primary" onClick={generate} disabled={generating} style={{ whiteSpace: "nowrap" }}>
          {generating ? "Generating…" : "+ Generate Code"}
        </button>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      {loading ? (
        <p style={{ color: "var(--muted)", fontSize: "0.875rem" }}>Loading…</p>
      ) : codes.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><Icon name="key" size={48} /></div>
          <p>No access codes yet. Generate one to invite a nurse.</p>
        </div>
      ) : (
        <div>
          {/* Active codes */}
          {active.length > 0 && (
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontSize: "0.7rem", fontWeight: 700, color: "var(--muted)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>
                Active ({active.length})
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {active.map(c => {
                  const st = statusStyle(c.status);
                  return (
                    <div key={c.id} style={{ background: "var(--card-bg)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "14px 18px", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                      <span style={{ fontFamily: "monospace", fontSize: "1.15rem", fontWeight: 700, color: "var(--teal)", letterSpacing: "0.1em", flex: "0 0 auto" }}>{c.code}</span>
                      <span style={{ background: st.bg, color: st.color, fontSize: "0.7rem", fontWeight: 700, padding: "2px 8px", borderRadius: 4, textTransform: "uppercase", flex: "0 0 auto" }}>{c.status}</span>
                      <div style={{ flex: 1, minWidth: 0, fontSize: "0.8rem", color: "var(--muted)" }}>
                        <span>Expires {fmtTs(c.expiresAt)}</span>
                        <span style={{ margin: "0 8px" }}>·</span>
                        <span>Created by {c.createdBy}</span>
                        <span style={{ margin: "0 8px" }}>·</span>
                        <span>{fmtTs(c.createdAt)}</span>
                      </div>
                      <div style={{ display: "flex", gap: 8, flex: "0 0 auto" }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => copy(c.code, c.id)} style={{ minWidth: 72 }}>
                          {copiedId === c.id ? "Copied!" : "Copy"}
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => revoke(c.id)} disabled={revoking === c.id}>
                          {revoking === c.id ? "…" : "Revoke"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Used / Expired codes */}
          {rest.length > 0 && (
            <div>
              <div style={{ fontSize: "0.7rem", fontWeight: 700, color: "var(--muted)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>
                History ({rest.length})
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
                  <thead>
                    <tr style={{ background: "var(--navy-mid)", borderBottom: "2px solid var(--border)" }}>
                      {["Code", "Status", "Created By", "Created", "Used By", "Used At"].map(h => (
                        <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 700, color: "var(--muted)", fontSize: "0.7rem", letterSpacing: "0.06em", textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rest.map((c, i) => {
                      const st = statusStyle(c.status);
                      return (
                        <tr key={c.id} style={{ borderBottom: "1px solid var(--border)", background: i % 2 === 0 ? "transparent" : "var(--navy-mid)" }}>
                          <td style={{ padding: "10px 12px", fontFamily: "monospace", fontWeight: 600, color: "var(--muted)", letterSpacing: "0.06em" }}>{c.code}</td>
                          <td style={{ padding: "10px 12px" }}>
                            <span style={{ background: st.bg, color: st.color, fontSize: "0.7rem", fontWeight: 700, padding: "2px 8px", borderRadius: 4, textTransform: "uppercase" }}>{c.status}</span>
                          </td>
                          <td style={{ padding: "10px 12px", color: "var(--text)" }}>{c.createdBy}</td>
                          <td style={{ padding: "10px 12px", color: "var(--muted)", whiteSpace: "nowrap" }}>{fmtTs(c.createdAt)}</td>
                          <td style={{ padding: "10px 12px", color: "var(--text)" }}>{c.usedBy || "—"}</td>
                          <td style={{ padding: "10px 12px", color: "var(--muted)", whiteSpace: "nowrap" }}>{c.usedAt ? fmtTs(c.usedAt) : "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// ADMIN: COMPLIANCE CENTER (Audit Trail · Access Logs · Permissions)
// ============================================================
function AdminCompliance({ user }) {
  const [tab, setTab] = useState("audit");

  // ── Audit Trail state ────────────────────────────────────────
  const [auditLogs, setAuditLogs]       = useState([]);
  const [auditTotal, setAuditTotal]     = useState(0);
  const [auditPage, setAuditPage]       = useState(1);
  const [auditPages, setAuditPages]     = useState(1);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditAction, setAuditAction]   = useState("");
  const [auditStart, setAuditStart]     = useState("");
  const [auditEnd, setAuditEnd]         = useState("");

  // ── Access Logs state ────────────────────────────────────────
  const [accessLogs, setAccessLogs]       = useState([]);
  const [accessTotal, setAccessTotal]     = useState(0);
  const [accessPage, setAccessPage]       = useState(1);
  const [accessPages, setAccessPages]     = useState(1);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessStart, setAccessStart]     = useState("");
  const [accessEnd, setAccessEnd]         = useState("");

  // ── Permissions state ────────────────────────────────────────
  const [admins, setAdmins]         = useState([]);
  const [permsLoading, setPermsLoading] = useState(false);
  const [editingId, setEditingId]   = useState(null);
  const [editDraft, setEditDraft]   = useState(null);
  const [savingPerm, setSavingPerm] = useState(false);
  const [permMsg, setPermMsg]       = useState(null);
  // ── Role change state ─────────────────────────────────────────
  const [roleChanging, setRoleChanging]     = useState(null); // id being demoted
  const [promoteEmail, setPromoteEmail]     = useState("");
  const [promoteResult, setPromoteResult]   = useState(null); // { user } or null
  const [promoteSearching, setPromoteSearching] = useState(false);
  const [promoteMsg, setPromoteMsg]         = useState(null);
  const [promoting, setPromoting]           = useState(false);

  const fetchAudit = useCallback(async (pg = 1) => {
    setAuditLoading(true);
    try {
      const params = { page: pg, limit: 50 };
      if (auditAction) params.action = auditAction;
      if (auditStart)  params.startDate = auditStart;
      if (auditEnd)    params.endDate   = auditEnd;
      const data = await Audit.logs(params);
      setAuditLogs(data.logs || []);
      setAuditTotal(data.total || 0);
      setAuditPage(data.page  || 1);
      setAuditPages(data.pages || 1);
    } catch (e) {
      console.error(e);
    } finally {
      setAuditLoading(false);
    }
  }, [auditAction, auditStart, auditEnd]);

  const fetchAccess = useCallback(async (pg = 1) => {
    setAccessLoading(true);
    try {
      const params = { page: pg, limit: 50 };
      if (accessStart) params.startDate = accessStart;
      if (accessEnd)   params.endDate   = accessEnd;
      const data = await Audit.access(params);
      setAccessLogs(data.logs || []);
      setAccessTotal(data.total || 0);
      setAccessPage(data.page  || 1);
      setAccessPages(data.pages || 1);
    } catch (e) {
      console.error(e);
    } finally {
      setAccessLoading(false);
    }
  }, [accessStart, accessEnd]);

  const fetchAdmins = useCallback(async () => {
    setPermsLoading(true);
    try {
      const data = await AdminPermissions.list();
      setAdmins(data.admins || []);
    } catch (e) {
      console.error(e);
    } finally {
      setPermsLoading(false);
    }
  }, []);

  useEffect(() => { if (tab === "audit")       fetchAudit(1);   }, [tab, fetchAudit]);
  useEffect(() => { if (tab === "access")      fetchAccess(1);  }, [tab, fetchAccess]);
  useEffect(() => { if (tab === "permissions") fetchAdmins();   }, [tab, fetchAdmins]);

  const startEdit = (admin) => {
    setEditingId(admin.id);
    setEditDraft({ ...admin.permissions });
    setPermMsg(null);
  };

  const cancelEdit = () => { setEditingId(null); setEditDraft(null); setPermMsg(null); };

  const demoteToNurse = async (admin) => {
    if (!window.confirm(`Demote ${admin.name} to Employee? They will lose all admin access and be signed out immediately.`)) return;
    setRoleChanging(admin.id);
    setPermMsg(null);
    try {
      await Nurses.changeRole(admin.id, 'nurse');
      setPermMsg({ type: "ok", text: `${admin.name} has been demoted to Employee.` });
      fetchAdmins();
    } catch (e) {
      setPermMsg({ type: "err", text: e.message || "Role change failed." });
    } finally {
      setRoleChanging(null);
    }
  };

  const searchPromote = async () => {
    if (!promoteEmail.trim()) return;
    setPromoteSearching(true);
    setPromoteResult(null);
    setPromoteMsg(null);
    try {
      const data = await Nurses.findByEmail(promoteEmail.trim());
      setPromoteResult(data.user);
    } catch (e) {
      setPromoteMsg({ type: "err", text: e.message || "User not found." });
    } finally {
      setPromoteSearching(false);
    }
  };

  const promoteToAdmin = async () => {
    if (!promoteResult) return;
    if (!window.confirm(`Promote ${promoteResult.name || promoteResult.email} to Staff (Admin)? They will get default restricted permissions.`)) return;
    setPromoting(true);
    setPromoteMsg(null);
    try {
      await Nurses.changeRole(promoteResult.id, 'admin');
      setPromoteMsg({ type: "ok", text: `${promoteResult.name || promoteResult.email} is now a Staff member. Set their permissions below.` });
      setPromoteResult(null);
      setPromoteEmail("");
      fetchAdmins();
    } catch (e) {
      setPromoteMsg({ type: "err", text: e.message || "Promotion failed." });
    } finally {
      setPromoting(false);
    }
  };

  const savePerm = async (adminId) => {
    setSavingPerm(true);
    setPermMsg(null);
    try {
      await AdminPermissions.update(adminId, editDraft);
      setPermMsg({ type: "ok", text: "Permissions saved." });
      setEditingId(null);
      setEditDraft(null);
      fetchAdmins();
    } catch (e) {
      setPermMsg({ type: "err", text: e.message || "Save failed." });
    } finally {
      setSavingPerm(false);
    }
  };

  const PERM_LABELS = [
    { key: "canResearch",      label: "Nurse Research",       desc: "View and search nurse profiles" },
    { key: "canAvailability",  label: "Nurse Availability",   desc: "Toggle nurse availability status" },
    { key: "canInterests",     label: "Contract Interests",   desc: "View and manage contract interest lists" },
    { key: "canContracts",     label: "Posted Contracts",     desc: "View posted contracts and analytics" },
    { key: "canAddContract",   label: "Post Contracts",       desc: "Create and publish new contracts" },
    { key: "canNotifications", label: "Notifications",        desc: "Send mass emails and payroll reminders" },
    { key: "canMessages",      label: "Messages",             desc: "Access the internal messaging system" },
    { key: "canCompliance",    label: "Compliance Center",    desc: "View audit trails and access logs" },
    { key: "canInvite",        label: "Access Codes",         desc: "Generate and revoke nurse invite codes" },
  ];

  const actionBadge = (action) => {
    if (action.includes("fail"))   return { bg: "rgba(220,38,38,0.1)",  color: "#DC2626", label: "FAILED" };
    if (action.startsWith("auth")) return { bg: "rgba(14,116,144,0.1)", color: "#0E7490", label: "AUTH" };
    if (action.startsWith("admin")) return { bg: "rgba(217,119,6,0.1)", color: "#D97706", label: "ADMIN" };
    return { bg: "rgba(0,51,102,0.07)", color: "#003366", label: "ACTION" };
  };

  const fmtTs = (ts) => {
    if (!ts) return "—";
    const d = new Date(ts);
    return d.toLocaleString("en-CA", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: "1.35rem", fontWeight: 800, color: "var(--teal)", marginBottom: 4 }}>Compliance Center</h2>
        <p style={{ color: "var(--muted)", fontSize: "0.875rem" }}>Audit trails, access logs, and admin permission controls — required under Canadian privacy law (PIPEDA).</p>
      </div>

      {/* Tab bar */}
      <div style={{ display: "flex", gap: 4, background: "var(--navy-mid)", borderRadius: "var(--radius-sm)", padding: 4, marginBottom: 28, width: "fit-content" }}>
        {[
          { id: "audit",       label: "Audit Trail",   icon: "clipboard" },
          { id: "access",      label: "Access Logs",   icon: "log" },
          { id: "permissions", label: "Permissions",   icon: "key" },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 18px", borderRadius: 8, border: "none",
              fontFamily: "var(--font)", fontSize: "0.84rem", fontWeight: 600, cursor: "pointer",
              background: tab === t.id ? "var(--teal)" : "transparent",
              color: tab === t.id ? "#fff" : "var(--muted)",
              transition: "all 0.15s",
              boxShadow: tab === t.id ? "0 2px 8px rgba(0,51,102,0.2)" : "none" }}>
            <Icon name={t.icon} size={15} /> {t.label}
          </button>
        ))}
      </div>

      {/* ── AUDIT TRAIL ── */}
      {tab === "audit" && (
        <div>
          {/* Filters */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 18, alignItems: "flex-end" }}>
            <div>
              <label style={{ fontSize: "0.7rem" }}>Action keyword</label>
              <input style={{ maxWidth: 200 }} placeholder="e.g. auth.login" value={auditAction}
                onChange={e => setAuditAction(e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: "0.7rem" }}>From date</label>
              <input type="date" style={{ maxWidth: 160 }} value={auditStart} onChange={e => setAuditStart(e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: "0.7rem" }}>To date</label>
              <input type="date" style={{ maxWidth: 160 }} value={auditEnd} onChange={e => setAuditEnd(e.target.value)} />
            </div>
            <button className="btn btn-primary btn-sm" onClick={() => fetchAudit(1)} style={{ marginBottom: 0 }}>
              <Icon name="filter" size={14} /> Apply
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => { setAuditAction(""); setAuditStart(""); setAuditEnd(""); }}>Clear</button>
          </div>

          <div style={{ fontSize: "0.78rem", color: "var(--muted)", marginBottom: 12 }}>
            {auditTotal.toLocaleString()} total entries — page {auditPage} of {auditPages}
          </div>

          {auditLoading ? (
            <p style={{ color: "var(--muted)", fontSize: "0.875rem" }}>Loading…</p>
          ) : auditLogs.length === 0 ? (
            <p style={{ color: "var(--muted)", fontSize: "0.875rem" }}>No audit entries found.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
                <thead>
                  <tr style={{ background: "var(--navy-mid)", borderBottom: "2px solid var(--border)" }}>
                    {["Date / Time", "Actor", "Action", "Target", "IP Address"].map(h => (
                      <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 700, color: "var(--muted)", fontSize: "0.7rem", letterSpacing: "0.06em", textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map((log, i) => {
                    const badge = actionBadge(log.action);
                    return (
                      <tr key={log.id} style={{ borderBottom: "1px solid var(--border)", background: i % 2 === 0 ? "transparent" : "rgba(0,51,102,0.02)" }}>
                        <td style={{ padding: "9px 12px", color: "var(--muted)", whiteSpace: "nowrap", fontFamily: "var(--mono)", fontSize: "0.78rem" }}>{fmtTs(log.createdAt)}</td>
                        <td style={{ padding: "9px 12px" }}>
                          <div style={{ fontWeight: 600, color: "var(--white)" }}>{log.actorName}</div>
                          {log.actorEmail && <div style={{ fontSize: "0.72rem", color: "var(--muted)" }}>{log.actorEmail}</div>}
                        </td>
                        <td style={{ padding: "9px 12px" }}>
                          <span style={{ background: badge.bg, color: badge.color, padding: "2px 8px", borderRadius: 99, fontSize: "0.7rem", fontWeight: 700, fontFamily: "var(--mono)" }}>
                            {log.action}
                          </span>
                        </td>
                        <td style={{ padding: "9px 12px", color: "var(--muted)", fontFamily: "var(--mono)", fontSize: "0.75rem", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {log.target || "—"}
                        </td>
                        <td style={{ padding: "9px 12px", color: "var(--muted)", fontFamily: "var(--mono)", fontSize: "0.75rem" }}>{log.ipAddress || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {auditPages > 1 && (
            <div style={{ display: "flex", gap: 8, marginTop: 16, alignItems: "center" }}>
              <button className="btn btn-sm btn-ghost" disabled={auditPage === 1} onClick={() => fetchAudit(auditPage - 1)}>← Prev</button>
              <span style={{ fontSize: "0.82rem", color: "var(--muted)" }}>Page {auditPage} / {auditPages}</span>
              <button className="btn btn-sm btn-ghost" disabled={auditPage === auditPages} onClick={() => fetchAudit(auditPage + 1)}>Next →</button>
            </div>
          )}
        </div>
      )}

      {/* ── ACCESS LOGS ── */}
      {tab === "access" && (
        <div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 18, alignItems: "flex-end" }}>
            <div>
              <label style={{ fontSize: "0.7rem" }}>From date</label>
              <input type="date" style={{ maxWidth: 160 }} value={accessStart} onChange={e => setAccessStart(e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: "0.7rem" }}>To date</label>
              <input type="date" style={{ maxWidth: 160 }} value={accessEnd} onChange={e => setAccessEnd(e.target.value)} />
            </div>
            <button className="btn btn-primary btn-sm" onClick={() => fetchAccess(1)}>
              <Icon name="filter" size={14} /> Apply
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => { setAccessStart(""); setAccessEnd(""); }}>Clear</button>
          </div>

          <div style={{ fontSize: "0.78rem", color: "var(--muted)", marginBottom: 12 }}>
            {accessTotal.toLocaleString()} access events — page {accessPage} of {accessPages}
          </div>

          {/* Summary cards */}
          {!accessLoading && accessLogs.length > 0 && (
            <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
              {[
                { label: "Login Success", count: accessLogs.filter(l => l.action === "auth.login").length, color: "#10B981" },
                { label: "Login Failed",  count: accessLogs.filter(l => l.action === "auth.login.fail").length, color: "#DC2626" },
                { label: "Registrations", count: accessLogs.filter(l => l.action === "auth.register").length, color: "#0E7490" },
                { label: "Logouts",       count: accessLogs.filter(l => l.action === "auth.logout").length, color: "var(--muted)" },
              ].map(s => (
                <div key={s.label} style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 10, padding: "12px 18px", minWidth: 120 }}>
                  <div style={{ fontSize: "1.4rem", fontWeight: 800, color: s.color }}>{s.count}</div>
                  <div style={{ fontSize: "0.72rem", color: "var(--muted)", fontWeight: 600 }}>{s.label}</div>
                  <div style={{ fontSize: "0.67rem", color: "var(--muted)", marginTop: 2 }}>this page</div>
                </div>
              ))}
            </div>
          )}

          {accessLoading ? (
            <p style={{ color: "var(--muted)", fontSize: "0.875rem" }}>Loading…</p>
          ) : accessLogs.length === 0 ? (
            <p style={{ color: "var(--muted)", fontSize: "0.875rem" }}>No access events found.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
                <thead>
                  <tr style={{ background: "var(--navy-mid)", borderBottom: "2px solid var(--border)" }}>
                    {["Date / Time", "User", "Role", "Event", "IP Address", "Status"].map(h => (
                      <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 700, color: "var(--muted)", fontSize: "0.7rem", letterSpacing: "0.06em", textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {accessLogs.map((log, i) => (
                    <tr key={log.id} style={{ borderBottom: "1px solid var(--border)", background: log.failed ? "rgba(220,38,38,0.03)" : i % 2 === 0 ? "transparent" : "rgba(0,51,102,0.02)" }}>
                      <td style={{ padding: "9px 12px", color: "var(--muted)", whiteSpace: "nowrap", fontFamily: "var(--mono)", fontSize: "0.78rem" }}>{fmtTs(log.createdAt)}</td>
                      <td style={{ padding: "9px 12px" }}>
                        <div style={{ fontWeight: 600, color: "var(--white)" }}>{log.actorName}</div>
                        {log.actorEmail && <div style={{ fontSize: "0.72rem", color: "var(--muted)" }}>{log.actorEmail}</div>}
                      </td>
                      <td style={{ padding: "9px 12px" }}>
                        <span style={{ fontSize: "0.72rem", fontWeight: 700, padding: "2px 8px", borderRadius: 99,
                          background: log.actorRole === "admin" ? "rgba(0,51,102,0.08)" : "rgba(14,116,144,0.08)",
                          color: log.actorRole === "admin" ? "var(--teal)" : "var(--bc-teal)" }}>
                          {log.actorRole || "unknown"}
                        </span>
                      </td>
                      <td style={{ padding: "9px 12px", fontFamily: "var(--mono)", fontSize: "0.75rem", color: "var(--muted)" }}>{log.action}</td>
                      <td style={{ padding: "9px 12px", color: "var(--muted)", fontFamily: "var(--mono)", fontSize: "0.75rem" }}>{log.ipAddress || "—"}</td>
                      <td style={{ padding: "9px 12px" }}>
                        {log.failed
                          ? <span style={{ background: "rgba(220,38,38,0.1)", color: "#DC2626", padding: "2px 8px", borderRadius: 99, fontSize: "0.7rem", fontWeight: 700 }}>FAILED</span>
                          : <span style={{ background: "rgba(16,185,129,0.1)", color: "#10B981", padding: "2px 8px", borderRadius: 99, fontSize: "0.7rem", fontWeight: 700 }}>OK</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {accessPages > 1 && (
            <div style={{ display: "flex", gap: 8, marginTop: 16, alignItems: "center" }}>
              <button className="btn btn-sm btn-ghost" disabled={accessPage === 1} onClick={() => fetchAccess(accessPage - 1)}>← Prev</button>
              <span style={{ fontSize: "0.82rem", color: "var(--muted)" }}>Page {accessPage} / {accessPages}</span>
              <button className="btn btn-sm btn-ghost" disabled={accessPage === accessPages} onClick={() => fetchAccess(accessPage + 1)}>Next →</button>
            </div>
          )}
        </div>
      )}

      {/* ── PERMISSIONS ── */}
      {tab === "permissions" && (
        <div>
          <div style={{ marginBottom: 20, padding: "14px 18px", background: "rgba(217,119,6,0.06)", border: "1px solid rgba(217,119,6,0.2)", borderRadius: 10, display: "flex", gap: 12, alignItems: "flex-start" }}>
            <Icon name="shield" size={18} color="#D97706" />
            <div>
              <div style={{ fontWeight: 700, color: "#D97706", fontSize: "0.84rem", marginBottom: 3 }}>Permission Control Center</div>
              <div style={{ fontSize: "0.8rem", color: "var(--muted)" }}>Only super-admins can modify permissions. Changes take effect on the admin's next login or page refresh. You cannot revoke your own super-admin status.</div>
            </div>
          </div>

          {permMsg && (
            <div style={{ marginBottom: 14, padding: "10px 16px", borderRadius: 8, fontSize: "0.84rem", fontWeight: 600,
              background: permMsg.type === "ok" ? "rgba(16,185,129,0.1)" : "rgba(220,38,38,0.1)",
              color: permMsg.type === "ok" ? "#10B981" : "#DC2626" }}>
              {permMsg.text}
            </div>
          )}

          {/* Promote Employee to Staff */}
          <div style={{ marginBottom: 24, padding: "18px 20px", background: "var(--card)", border: "1px solid var(--border)", borderRadius: "var(--radius)" }}>
            <div style={{ fontWeight: 700, color: "var(--white)", fontSize: "0.9rem", marginBottom: 4 }}>Promote Employee to Staff</div>
            <div style={{ fontSize: "0.78rem", color: "var(--muted)", marginBottom: 14 }}>Look up a nurse by email address to promote them to a Staff (Admin) account.</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
              <div style={{ flex: 1, minWidth: 220 }}>
                <label style={{ fontSize: "0.7rem" }}>Email address</label>
                <input
                  type="email"
                  placeholder="nurse@example.com"
                  value={promoteEmail}
                  onChange={e => { setPromoteEmail(e.target.value); setPromoteResult(null); setPromoteMsg(null); }}
                  onKeyDown={e => e.key === "Enter" && searchPromote()}
                />
              </div>
              <button className="btn btn-primary btn-sm" onClick={searchPromote} disabled={promoteSearching || !promoteEmail.trim()} style={{ marginBottom: 0 }}>
                {promoteSearching ? "Searching…" : "Look Up"}
              </button>
            </div>
            {promoteMsg && (
              <div style={{ marginTop: 10, padding: "8px 14px", borderRadius: 8, fontSize: "0.82rem", fontWeight: 600,
                background: promoteMsg.type === "ok" ? "rgba(16,185,129,0.1)" : "rgba(220,38,38,0.1)",
                color: promoteMsg.type === "ok" ? "#10B981" : "#DC2626" }}>
                {promoteMsg.text}
              </div>
            )}
            {promoteResult && (
              <div style={{ marginTop: 12, padding: "14px 16px", background: "var(--navy-mid)", borderRadius: 10, border: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontWeight: 700, color: "var(--white)", fontSize: "0.88rem" }}>{promoteResult.name || "—"}</div>
                  <div style={{ fontSize: "0.78rem", color: "var(--muted)" }}>{promoteResult.email}</div>
                  <span style={{ display: "inline-block", marginTop: 4, fontSize: "0.7rem", fontWeight: 700, padding: "2px 8px", borderRadius: 99, background: "rgba(14,116,144,0.1)", color: "#0E7490" }}>
                    {promoteResult.role === "admin" ? "Already a Staff member" : "Employee"}
                  </span>
                </div>
                {promoteResult.role !== "admin" && (
                  <button className="btn btn-primary btn-sm" onClick={promoteToAdmin} disabled={promoting}>
                    {promoting ? "Promoting…" : "Promote to Staff"}
                  </button>
                )}
              </div>
            )}
          </div>

          {permsLoading ? (
            <p style={{ color: "var(--muted)", fontSize: "0.875rem" }}>Loading…</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {admins.map(admin => {
                const isEditing = editingId === admin.id;
                const isSelf    = admin.id === user?.id;
                const draft     = isEditing ? editDraft : admin.permissions;

                return (
                  <div key={admin.id} style={{ background: "var(--card)", border: `1px solid ${isEditing ? "var(--teal)" : "var(--border)"}`, borderRadius: "var(--radius)", padding: "20px 22px", boxShadow: isEditing ? "var(--shadow-md)" : "var(--shadow-sm)" }}>
                    {/* Admin header */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: isEditing ? 20 : 0, flexWrap: "wrap", gap: 10 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{ width: 40, height: 40, borderRadius: "50%", background: "var(--teal)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: "0.9rem", flexShrink: 0 }}>
                          {(admin.name || admin.email || "A")[0].toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, color: "var(--white)", fontSize: "0.92rem" }}>
                            {admin.name}
                            {isSelf && <span style={{ marginLeft: 8, fontSize: "0.68rem", fontWeight: 700, padding: "2px 8px", borderRadius: 99, background: "rgba(0,51,102,0.1)", color: "var(--teal)" }}>You</span>}
                          </div>
                          <div style={{ fontSize: "0.78rem", color: "var(--muted)" }}>{admin.email}</div>
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        {admin.permissions.isSuperAdmin && (
                          <span style={{ fontSize: "0.7rem", fontWeight: 800, padding: "3px 10px", borderRadius: 99, background: "rgba(0,51,102,0.1)", color: "var(--teal)", letterSpacing: "0.06em" }}>SUPER ADMIN</span>
                        )}
                        {!isEditing && !isSelf && (
                          <button className="btn btn-danger btn-sm" onClick={() => demoteToNurse(admin)} disabled={roleChanging === admin.id}>
                            {roleChanging === admin.id ? "…" : "Demote to Employee"}
                          </button>
                        )}
                        {!isEditing && (
                          <button className="btn btn-outline btn-sm" onClick={() => startEdit(admin)}>
                            Edit Permissions
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Permission toggles (editing mode) */}
                    {isEditing && editDraft && (
                      <>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10, marginBottom: 18 }}>
                          {/* Super-admin toggle */}
                          <div style={{ gridColumn: "1/-1", padding: "12px 16px", background: "rgba(217,119,6,0.05)", border: "1px solid rgba(217,119,6,0.2)", borderRadius: 10 }}>
                            <label style={{ display: "flex", alignItems: "center", gap: 12, cursor: isSelf ? "not-allowed" : "pointer", textTransform: "none", letterSpacing: 0, fontSize: "0.88rem", fontWeight: 700, color: "#D97706" }}>
                              <input type="checkbox" checked={editDraft.isSuperAdmin || false}
                                disabled={isSelf}
                                onChange={e => setEditDraft(d => ({ ...d, isSuperAdmin: e.target.checked }))}
                                style={{ width: 16, height: 16, cursor: isSelf ? "not-allowed" : "pointer" }} />
                              Super Admin
                              <span style={{ fontSize: "0.75rem", color: "var(--muted)", fontWeight: 500 }}>— full access + can manage permissions</span>
                            </label>
                          </div>

                          {PERM_LABELS.map(p => (
                            <div key={p.key} style={{ padding: "12px 16px", background: "var(--navy-mid)", borderRadius: 10, border: "1px solid var(--border)" }}>
                              <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", textTransform: "none", letterSpacing: 0 }}>
                                <input type="checkbox" checked={editDraft[p.key] ?? true}
                                  onChange={e => setEditDraft(d => ({ ...d, [p.key]: e.target.checked }))}
                                  style={{ width: 16, height: 16, marginTop: 2, cursor: "pointer", flexShrink: 0 }} />
                                <div>
                                  <div style={{ fontSize: "0.86rem", fontWeight: 700, color: "var(--white)", marginBottom: 2 }}>{p.label}</div>
                                  <div style={{ fontSize: "0.75rem", color: "var(--muted)", fontWeight: 400 }}>{p.desc}</div>
                                </div>
                              </label>
                            </div>
                          ))}
                        </div>

                        {admin.updatedAt && (
                          <div style={{ fontSize: "0.72rem", color: "var(--muted)", marginBottom: 14 }}>
                            Last updated {fmtTs(admin.updatedAt)}{admin.updatedBy ? ` by ${admin.updatedBy}` : ""}
                          </div>
                        )}

                        <div style={{ display: "flex", gap: 10 }}>
                          <button className="btn btn-primary btn-sm" onClick={() => savePerm(admin.id)} disabled={savingPerm}>
                            {savingPerm ? "Saving…" : "Save Permissions"}
                          </button>
                          <button className="btn btn-ghost btn-sm" onClick={cancelEdit}>Cancel</button>
                        </div>
                      </>
                    )}

                    {/* Read-only permission chips */}
                    {!isEditing && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 12 }}>
                        {PERM_LABELS.map(p => (
                          <span key={p.key} style={{
                            padding: "3px 10px", borderRadius: 99, fontSize: "0.7rem", fontWeight: 700,
                            background: admin.permissions[p.key] ? "rgba(16,185,129,0.1)" : "rgba(220,38,38,0.07)",
                            color: admin.permissions[p.key] ? "#10B981" : "#DC2626",
                            border: `1px solid ${admin.permissions[p.key] ? "rgba(16,185,129,0.2)" : "rgba(220,38,38,0.15)"}`,
                          }}>
                            {admin.permissions[p.key] ? "✓" : "✗"} {p.label}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// MAIN APP
// ============================================================
export default function App() {
  const [user, setUser] = useState(null);
  const [page, setPage] = useState("dashboard");
  const [bootstrapping, setBootstrapping] = useState(true);
  const [nurseProfileTarget, setNurseProfileTarget] = useState(null);
  const [nurseProfileOrigin, setNurseProfileOrigin] = useState("research");
  const [resetToken, setResetToken] = useState(() => new URLSearchParams(window.location.search).get('reset'));
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Restore session if a refresh token persists in localStorage
  useEffect(() => {
    if (!getRefreshToken()) { setBootstrapping(false); return; }
    Nurses.me()
      .then(res => setUser(res.user))
      .catch(() => clearTokens())
      .finally(() => setBootstrapping(false));
  }, []);

  const nurseNav = [
    { id: "dashboard",       label: "Dashboard",       icon: "pulse" },
    { id: "contracts",       label: "Contract Board",  icon: "briefcase" },
    { id: "documents",       label: "Documents",       icon: "file" },
    { id: "schedules",       label: "Schedules",       icon: "clock" },
    { id: "profile",         label: "My Profile",      icon: "user" },
    { id: "notifications",   label: "Notifications",   icon: "bell" },
    { id: "messages",        label: "Messages",        icon: "bell" },
  ];

  const perms = user?.permissions || null;
  const hasPerm = (key) => !perms || perms[key] !== false;

  const adminNavAll = [
    { id: "dashboard",         label: "Dashboard",           icon: "pulse",      perm: null },
    { id: "research",          label: "Nurse Research",       icon: "user",       perm: "canResearch" },
    { id: "availability",      label: "Nurse Availability",   icon: "check",      perm: "canAvailability" },
    { id: "interests",         label: "Contract Interests",   icon: "bell",       perm: "canInterests" },
    { id: "posted-contracts",  label: "Posted Contracts",     icon: "briefcase",  perm: "canContracts" },
    { id: "add-contract",      label: "Post Contract",        icon: "plus",       perm: "canAddContract" },
    { id: "notifications",     label: "Notifications",        icon: "bell",       perm: "canNotifications" },
    { id: "messages",          label: "Messages",             icon: "bell",       perm: "canMessages" },
    { id: "access-codes",      label: "Access Codes",         icon: "key",        perm: "canInvite" },
    { id: "compliance",        label: "Compliance",           icon: "shield",     perm: "canCompliance" },
  ];

  const adminNav = adminNavAll.filter(n => !n.perm || hasPerm(n.perm));

  if (bootstrapping) {
    return (
      <>
        <style>{styles}</style>
        <div className="auth-screen"><p className="text-muted">Loading…</p></div>
      </>
    );
  }

  if (!user && resetToken) return (
    <>
      <style>{styles}</style>
      <ResetPasswordView token={resetToken} onDone={() => { window.history.replaceState({}, '', window.location.pathname); setResetToken(null); }} />
    </>
  );

  if (!user) return (
    <>
      <style>{styles}</style>
      <AuthScreen onLogin={(u) => { setUser(u); setPage("dashboard"); }} />
    </>
  );

  const nav = user.role === "admin" ? adminNav : nurseNav;

  const navigate = (id) => { setPage(id); setMobileNavOpen(false); };

  const logout = async () => {
    await Auth.logout();
    setUser(null);
    setPage("dashboard");
    setMobileNavOpen(false);
  };

  const renderPage = () => {
    if (user.role === "admin") {
      if (page === "research")          return <AdminResearch onOpenNurse={(n) => { setNurseProfileTarget(n); setNurseProfileOrigin("research"); setPage("nurse-profile"); }} />;
      if (page === "nurse-profile" && nurseProfileTarget) return <NurseProfilePage nurse={nurseProfileTarget} onBack={() => { setNurseProfileTarget(null); setPage(nurseProfileOrigin || "research"); }} />;
      if (page === "availability")      return <AdminAvailability />;
      if (page === "interests")         return <AdminInterests onOpenNurse={(n) => { setNurseProfileTarget(n); setNurseProfileOrigin("interests"); setPage("nurse-profile"); }} />;
      if (page === "posted-contracts")  return <AdminPostedContracts onOpenNurse={(n) => { setNurseProfileTarget(n); setNurseProfileOrigin("posted-contracts"); setPage("nurse-profile"); }} />;
      if (page === "add-contract")      return <AdminAddContract />;
      if (page === "notifications")     return <AdminNotifications />;
      if (page === "messages")          return <Messages user={user} />;
      if (page === "access-codes")      return <AdminAccessCodes />;
      if (page === "compliance")        return <AdminCompliance user={user} />;
      if (page === "profile")           return <NurseProfile user={user} setUser={setUser} />;
      return <AdminDashboard setPage={setPage} />;
    }
    if (page === "contracts")     return <ContractBoard user={user} />;
    if (page === "profile")       return <NurseProfile user={user} setUser={setUser} />;
    if (page === "documents")     return <NurseDocuments />;
    if (page === "schedules")     return <NurseSchedules />;
    if (page === "notifications") return <NurseNotificationSettings />;
    if (page === "messages")      return <Messages user={user} />;
    return <NurseDashboard user={user} setUser={setUser} setPage={setPage} />;
  };

  return (
    <>
      <style>{styles}</style>
      <div className="app">
        <a href="#main-content" className="skip-link">Skip to main content</a>
        <div className="topbar">
          <div className="logo">
            <div className="logo-badge">NLC</div>
            <div className="logo-text">
              <span className="logo-name">Portal</span>
              <span className="logo-sub">British Columbia</span>
            </div>
          </div>
          <div className="topbar-right">
            <span className="badge-pill">{user.role === "admin" ? "Admin" : "Nurse"}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <button className="avatar" aria-label="Edit profile" onClick={() => navigate("profile")}>{(user.name || user.firstName || user.email || "U")[0].toUpperCase()}</button>
              <button onClick={() => navigate("profile")} aria-label="Profile settings" title="Profile settings" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)", padding: 4, lineHeight: 0, borderRadius: 6, transition: "color 0.15s" }} onMouseEnter={e => e.currentTarget.style.color = "var(--teal)"} onMouseLeave={e => e.currentTarget.style.color = "var(--muted)"}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
              </button>
              <button
                className="topbar-hamburger"
                aria-label="Open navigation menu"
                aria-expanded={mobileNavOpen}
                onClick={() => setMobileNavOpen(true)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile nav drawer */}
        {mobileNavOpen && (
          <>
            <div className="mobile-nav-overlay" onClick={() => setMobileNavOpen(false)} aria-hidden="true" />
            <nav className="mobile-nav-drawer" aria-label="Mobile navigation">
              <div className="mobile-nav-header">
                <div className="logo">
                  <div className="logo-badge">NLC</div>
                  <div className="logo-text">
                    <span className="logo-name">Portal</span>
                    <span className="logo-sub">British Columbia</span>
                  </div>
                </div>
                <button className="mobile-nav-close" onClick={() => setMobileNavOpen(false)} aria-label="Close navigation">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
              <div className="mobile-nav-body">
                <div className="nav-section">Navigation</div>
                {nav.map(n => (
                  <button key={n.id} className={`nav-item ${page === n.id ? "active" : ""}`} onClick={() => navigate(n.id)}>
                    <Icon name={n.icon} size={17} /> {n.label}
                  </button>
                ))}
              </div>
              <div className="mobile-nav-footer">
                <button className="nav-item logout-btn" style={{ width: "100%" }} onClick={logout}>
                  <Icon name="logout" size={17} /> Sign Out
                </button>
              </div>
            </nav>
          </>
        )}

        <div className="main-layout">
          <nav className="sidebar">
            <div className="nav-section">Navigation</div>
            {nav.map(n => (
              <button key={n.id} className={`nav-item ${page === n.id ? "active" : ""}`} onClick={() => setPage(n.id)}>
                <Icon name={n.icon} size={17} /> {n.label}
              </button>
            ))}
            <div className="nav-divider" />
            <button className="nav-item logout-btn" onClick={logout}>
              <Icon name="logout" size={17} /> Sign Out
            </button>
            <div className="sidebar-scene" aria-hidden="true">
              <svg viewBox="0 0 260 110" preserveAspectRatio="xMidYMax slice" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="sidebar-sky" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#E8EFF5"/>
                    <stop offset="100%" stopColor="#BDD0E2"/>
                  </linearGradient>
                </defs>
                <rect width="260" height="110" fill="url(#sidebar-sky)"/>
                <path d="M80,35 L98,18 L116,35Z" fill="rgba(255,255,255,0.65)"/>
                <path d="M158,28 L176,10 L194,28Z" fill="rgba(255,255,255,0.55)"/>
                <path d="M0,78 L30,50 L62,65 L98,32 L130,58 L162,36 L196,56 L226,42 L260,60 L260,110 L0,110Z" fill="rgba(0,51,102,0.13)"/>
                <path d="M0,90 L36,66 L70,80 L104,58 L136,75 L168,60 L202,78 L232,64 L260,76 L260,110 L0,110Z" fill="rgba(0,51,102,0.24)"/>
                <path d="M0,102 Q65,95 130,100 Q195,106 260,97 L260,110 L0,110Z" fill="rgba(0,51,102,0.42)"/>
              </svg>
              <span className="sidebar-scene-label">British Columbia</span>
            </div>
          </nav>
          <main id="main-content" className="content">{renderPage()}</main>
        </div>
        <footer style={{ borderTop: "1px solid var(--border)", padding: "10px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap", background: "var(--navy-mid)", fontSize: "0.72rem", color: "var(--muted)" }}>
          <span>© {new Date().getFullYear()} NLC Staffing Inc. All rights reserved.</span>
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Icon name="shield" size={12} color="var(--bc-teal)" />
            AES-256 Encrypted · TLS 1.3 · SOC 2 Compliant · Canadian Privacy Standards
          </span>
        </footer>
      </div>
    </>
  );
}
